// =============================================
// Outline Generator + KG Distribution Service
// v3.0: Intent-based outline + KG data distribution to sections
// =============================================

import { supabase } from "@/integrations/supabase/client";
import { parseLLMJSON, getLLMProvider, getStageConfig } from "@/services/llm";
import { loadPrompt, fillTemplate } from "@/services/llm/promptLoader";
import type { FanOut, MicroArea, AnchorCandidate } from "./queryFanOut";
import type { KnowledgeGraph, KGEntity, KGFact, KGIdeation, KGRelationship } from "./knowledgeGraph";
import { normalizeForDedup, jaccardSimilarity } from "./textUtils";
import { logStage, logIntegrity, logPublic, logDebug, logVerbose, logError } from "./contentLogger";

// =============================================
// Types
// =============================================

export interface OutlineH3 {
  header: string;
  format: "question" | "context";
  source_paa?: string;
}

export interface OutlineSection {
  type: "intro" | "h2";
  order: number;
  header: string | null;
  source_area: string | null;
  source_intent: string;
  section_type: "full" | "context";
  grouped_areas?: string[];
  context_note?: string;
  h3s: OutlineH3[];
}

export interface SectionDistribution {
  // KG placeholder IDs (ent_001, ent_map_5) — uzywane przez draftGenerator do
  // match w kg.entities w pamieci. NIE zmienia sie, zero regresji.
  entity_ids: string[];
  fact_ids: string[];
  ideation_ids: string[];
  relationship_ids: string[];
  // UUID z thematic_entities (DB) — dodawane przez enrichEntityIds po Micro-EAV.
  // Downstream (analytics, re-gen per sekcja, EAV dociaganie) moze siegnac do DB.
  // Moze byc krotsze niz entity_ids (NER entities nie maja odpowiednika w DB).
  entity_db_ids?: string[];
}

export interface Outline {
  sections: OutlineSection[];
  distribution: Record<number, SectionDistribution>;
}

const OUTLINE_SCHEMA = {
  type: "object" as const,
  properties: {
    sections: {
      type: "array" as const,
      items: {
        type: "object" as const,
        properties: {
          type: { type: "string" as const, enum: ["intro", "h2"] },
          order: { type: "number" as const },
          header: { type: "string" as const },
          source_area: { type: "string" as const },
          source_intent: { type: "string" as const },
          section_type: { type: "string" as const, enum: ["full", "context"] },
          grouped_areas: { type: "array" as const, items: { type: "string" as const } },
          context_note: { type: "string" as const },
          h3s: {
            type: "array" as const,
            items: {
              type: "object" as const,
              properties: {
                header: { type: "string" as const },
                format: { type: "string" as const, enum: ["question", "context"] },
                source_paa: { type: "string" as const },
              },
              required: ["header", "format", "source_paa"] as const,
              additionalProperties: false as const,
            },
          },
        },
        required: ["type", "order", "header", "source_area", "source_intent", "section_type", "grouped_areas", "context_note", "h3s"] as const,
        additionalProperties: false as const,
      },
    },
  },
  required: ["sections"] as const,
  additionalProperties: false as const,
};

const DISTRIBUTION_SCHEMA = {
  type: "object" as const,
  properties: {
    assignments: {
      type: "array" as const,
      items: {
        type: "object" as const,
        properties: {
          section_order: { type: "number" as const },
          entity_ids: { type: "array" as const, items: { type: "string" as const } },
          fact_ids: { type: "array" as const, items: { type: "string" as const } },
          ideation_ids: { type: "array" as const, items: { type: "string" as const } },
          relationship_ids: { type: "array" as const, items: { type: "string" as const } },
        },
        required: ["section_order", "entity_ids", "fact_ids", "ideation_ids", "relationship_ids"] as const,
        additionalProperties: false as const,
      },
    },
  },
  required: ["assignments"] as const,
  additionalProperties: false as const,
};

// normalizeForDedup imported from ./textUtils

// =============================================
// Main function
// =============================================

// =============================================
// Rule 14 LLM-judge guard (v11)
// =============================================

interface Rule14ValidationResult {
  valid: boolean;
  illegitimateH2s: Array<{ title: string; reason: string }>;
  judgeCostUsd: number;
}

const RULE14_JUDGE_SCHEMA = {
  type: "object" as const,
  properties: {
    judgments: {
      type: "array" as const,
      items: {
        type: "object" as const,
        properties: {
          h2_title: { type: "string" as const },
          is_legitimate_anchor: { type: "boolean" as const },
          reason: { type: "string" as const },
        },
        required: ["h2_title", "is_legitimate_anchor", "reason"] as const,
        additionalProperties: false as const,
      },
    },
  },
  required: ["judgments"] as const,
  additionalProperties: false as const,
};

/**
 * Rule 14 runtime guard — semantic-judgment validation.
 *
 * Druga LLM (cheap call, gpt-4.1-mini) ocenia kazdy H2 zawierajacy primary_entity
 * pytaniem "czy ten H2 dziala IDENTYCZNIE bez primary_entity?":
 *  - YES → keyword stuffing (illegitimate)
 *  - NO  → topical anchor (legitimate)
 *
 * Agnostyczne wzgledem jezyka i niszy — judge LLM rozumie semantic function
 * niezaleznie czy outline jest PL/EN/DE, niezaleznie czy mapa to growth hacking,
 * kulinarna, finansowa, prawna. Sub-entities z KG distribution sa tez kontekstem
 * dla judge'a (legitymne anchors gdy w H2 — np. nazwiska tworcow).
 */
async function validateOutlineRule14(
  h2Sections: Array<{ header: string }>,
  primaryEntity: string,
  documentRole: string,
  canonicalQuestion: string,
  subEntities: string[],
  mapId: string,
  nodeId: string,
): Promise<Rule14ValidationResult> {
  const peLower = (primaryEntity || "").toLowerCase().trim();
  if (!peLower) {
    return { valid: true, illegitimateH2s: [], judgeCostUsd: 0 };
  }

  // Filter H2s containing primary_entity — multi-word PE token-set match (v13b fix).
  // Stary substring match (header.includes(peLower)) failowal dla multi-word PE jak
  // "ICE RICE PIE" gdy H2 zawieral tylko "ICE" — early-exit valid=true, judge skipped.
  // Token-set binary: ANY token (>=3 chars) z PE found w H2 header = candidate for judge.
  const peTokens = peLower.split(/\s+/).filter((t) => t.length >= 3);
  const tokensForMatch = peTokens.length > 0 ? peTokens : [peLower];
  const candidatesH2 = h2Sections.filter((h2) => {
    if (!h2.header) return false;
    const headerLower = h2.header.toLowerCase();
    return tokensForMatch.some((token) => headerLower.includes(token));
  });
  if (candidatesH2.length === 0) {
    // No PE token in any H2 = trivially valid (no keyword stuffing possible)
    return { valid: true, illegitimateH2s: [], judgeCostUsd: 0 };
  }

  const subEntityLines = subEntities.length > 0
    ? subEntities.map((e) => `  - ${e}`).join("\n")
    : "  (brak)";

  const judgePrompt = `Ocen czy primary_entity w kazdym H2 ponizej pelni rolę topical anchor (legitymne) czy keyword stuffing (illegitymne).

Topical anchor jest legitymny TYLKO w 3 sytuacjach:
A) Sekcja DEFINIUJE primary_entity (np. "Co to jest [PE]" — zwykle 1 H2 w outline)
B) Sekcja explicit POROWNUJE primary_entity z konkretnie nazwana alternatywa (np. "[PE] vs [konkretny_konkurent]")
C) Sekcja dotyczy KONKRETNEGO sub-entity ktore zawiera primary_entity w nazwie (np. "Cialdini" w artykule o "psychologia perswazji" — sub-entity z KG)

W KAZDYM INNYM przypadku primary_entity w H2 jest keyword stuffing — nawet jesli H2 ma legitymny atrybut/mechanizm/aktor anchor (np. "Cechy [PE]", "Geneza [PE]", "Przyklady [PE]", "Ograniczenia [PE]" — wszystkie keyword stuffing, primary_entity jest tu PREFIKSEM, nie anchorem).

KRYTYCZNY TEST:
"Czy ten H2 powtarza schemat '[primary_entity] + abstract atrybut' (cechy/geneza/przyklady/ograniczenia/zastosowania/korzysci/wady)?"
- TAK → keyword stuffing (is_legitimate_anchor=false), bo abstract atrybut sam wystarczy jako anchor (np. "Cechy" zamiast "Cechy [PE]")
- NIE → sprawdz dalsze 3 sytuacje A/B/C
- Jesli NIE pasuje do A/B/C → keyword stuffing

ANTI-PATTERN do reject (5 H2 z tym schematem):
"Czym sa [PE]" + "Cechy [PE]" + "Geneza [PE]" + "Przyklady [PE]" + "Ograniczenia [PE]"
= 5/5 H2 z PE, kazdy z abstract atrybutem. Tylko PIERWSZE jest legitymne (definicja). Pozostale 4 = keyword stuffing.

LEGITYMNY WZORZEC (1 H2 z PE):
"Czym sa [PE]" + "Eksperymentowanie zamiast kampanii" + "Sean Ellis 2010, narodziny" + "Hotmail/Dropbox/Airbnb — kanoniczne case" + "Kiedy nie warto"
= 1/5 H2 z PE (tylko definicja), 4/5 anchor w realnym scope.

Kontekst:
- primary_entity: "${primaryEntity}"
- canonical_question: "${canonicalQuestion}"
- document_role: "${documentRole}"
- sub-entities z KG (te tez moga byc legitymnymi anchors gdy w H2):
${subEntityLines}
- wszystkie H2 outline (kontekst):
${h2Sections.map((h2, i) => `${i + 1}. ${h2.header}`).join("\n")}

Oceniaj TYLKO te H2 ktore zawieraja primary_entity:
${candidatesH2.map((h2) => `- "${h2.header}"`).join("\n")}

Output JSON:
{ "judgments": [{ "h2_title": "...", "is_legitimate_anchor": true|false, "reason": "1-zdaniowy powod (jaki schemat = stuffing, lub ktory case A/B/C = legitymne)" }] }`;

  const judgeProvider = getLLMProvider("openai");
  const judgeResponse = await judgeProvider.call(judgePrompt, {
    model: "gpt-4.1-mini",
    temperature: 0.1,
    jsonSchema: RULE14_JUDGE_SCHEMA,
    step: "outline_rule_14_judge",
    mapId,
    nodeId,
  });

  let parsed: { judgments: Array<{ h2_title: string; is_legitimate_anchor: boolean; reason: string }> };
  try {
    parsed = parseLLMJSON(judgeResponse.text);
  } catch {
    // Judge LLM returned malformed JSON — skip validation (don't false-fail outline)
    logDebug("Outline", `Rule 14 judge: failed to parse judge response, treating as valid`);
    return { valid: true, illegitimateH2s: [], judgeCostUsd: judgeResponse.costUsd || 0 };
  }

  const illegitimate = (parsed.judgments || [])
    .filter((j) => !j.is_legitimate_anchor)
    .map((j) => ({ title: j.h2_title, reason: j.reason }));

  return {
    valid: illegitimate.length === 0,
    illegitimateH2s: illegitimate,
    judgeCostUsd: judgeResponse.costUsd || 0,
  };
}

// =============================================
// Rule 14b LLM-judge guard (v13b) — cross-outline structural opener diversity
// =============================================
// Detector phase (lingwistyczne regex anchor word match per role) → flagged H2s.
// Trigger gate: binary majority check (flagged > total/2). NIE numerical threshold.
// Judge phase (gpt-4.1-mini, temp 0.1): per-flagged-H2 verdict KOPIA/UNIKALNY +
// suggested anchor source (KG_entity / EAV_attribute / canonical_question_scope)
// + suggested anchor value. Judge dostaje flat KG + EAV pool (distribution = post-outline).
// Retry feedback wkomponowuje suggested anchors → wymusza concrete phrasing z KG/EAV.

interface Rule14bFlagged {
  index: number;
  header: string;
  matched_pattern: string;
}

interface Rule14bDetectorResult {
  triggered: boolean;
  generic_opener_count: number;
  total_h2_count: number;
  flagged_h2s: Rule14bFlagged[];
}

interface Rule14bJudgmentItem {
  title: string;
  suggested_anchor_source: string | null;
  suggested_anchor_value: string | null;
  reason: string;
}

interface Rule14bValidationResult {
  valid: boolean;
  detectorResult: Rule14bDetectorResult;
  judgeVerdict: "KOPIA" | "UNIKALNY" | "SKIPPED";
  flaggedAfterJudge: Rule14bJudgmentItem[];
  judgeCostUsd: number;
}

const RULE_14B_JUDGE_SCHEMA = {
  type: "object" as const,
  properties: {
    verdict: { type: "string" as const },
    judgments: {
      type: "array" as const,
      items: {
        type: "object" as const,
        properties: {
          h2_title: { type: "string" as const },
          verdict: { type: "string" as const },
          suggested_anchor_source: {
            anyOf: [{ type: "string" as const }, { type: "null" as const }],
          },
          suggested_anchor_value: {
            anyOf: [{ type: "string" as const }, { type: "null" as const }],
          },
          reason: { type: "string" as const },
        },
        required: ["h2_title", "verdict", "suggested_anchor_source", "suggested_anchor_value", "reason"] as const,
        additionalProperties: false as const,
      },
    },
  },
  required: ["verdict", "judgments"] as const,
  additionalProperties: false as const,
};

/**
 * Anchor word opener patterns per document_role.
 *
 * Lingwistyczne regex matches — NIE niche keywords. Patterns klasyfikuja
 * pierwsze 1-2 slowa H2 header jako "generic structural opener" (e.g.
 * definitional question opener / attributes label opener / provenance opener).
 *
 * PLACEHOLDER: sro-advisor will populate with verbatim patterns z v13 Rule 14b text.
 * Empty arrays per role → detector never triggers → judge SKIPPED → existing behavior.
 */
const RULE_14B_OPENER_PATTERNS: Record<string, RegExp[]> = {
  // PLACEHOLDER — sro-advisor provides patterns per role
  definition: [],
  comparison: [],
  how_to: [],
  troubleshooting: [],
  history: [],
  commercial: [],
  guide: [],
  glossary: [],
};

/**
 * Detector phase — regex match on H2 opener words.
 *
 * Strip optional [TAG] prefix (gdy LLM leaked source_area into header),
 * weź pierwsze 1-2 slowa + cały header lowercase, match przeciwko
 * RULE_14B_OPENER_PATTERNS[role]. Pierwszy match = flagged.
 *
 * Trigger gate: binary majority (flagged.length * 2 > total) — strictly more than half.
 * NIE arbitrary number — mathematical "majority" definition.
 */
function detectGenericOpeners(
  h2Sections: Array<{ header: string; source_area?: string | null }>,
  documentRole: string,
): Rule14bDetectorResult {
  const patterns = RULE_14B_OPENER_PATTERNS[documentRole] || [];
  const flagged_h2s: Rule14bFlagged[] = [];

  h2Sections.forEach((h2, index) => {
    if (!h2.header) return;
    const headerStripped = h2.header.replace(/^\[[A-Z_]+\]\s*/i, "").trim();
    const opener = headerStripped.split(/\s+/).slice(0, 2).join(" ").toLowerCase();
    const headerLower = headerStripped.toLowerCase();
    for (const pattern of patterns) {
      if (pattern.test(opener) || pattern.test(headerLower)) {
        flagged_h2s.push({ index, header: h2.header, matched_pattern: pattern.source });
        return;
      }
    }
  });

  const total_h2_count = h2Sections.filter((h) => h.header).length;
  // Binary majority — strictly more than half. Mathematical definition, NIE threshold.
  const triggered = total_h2_count > 0 && flagged_h2s.length * 2 > total_h2_count;

  return {
    triggered,
    generic_opener_count: flagged_h2s.length,
    total_h2_count,
    flagged_h2s,
  };
}

/**
 * Rule 14b runtime guard — cross-outline structural diversity judge.
 *
 * Detector binary triggers → judge LLM call. Judge per-flagged-H2 verdict
 * KOPIA (cross-mapa-kopiowalny generic) / UNIKALNY (tightly coupled z KG/EAV).
 * Per KOPIA H2 judge sugeruje konkretny anchor source + value z KG/EAV/scope.
 *
 * KG distribution per H2 NIE jest dostepna na tym etapie (distribution = post-outline),
 * dlatego judge dostaje flat KG sub-entity pool + EAV flat top 15 + canonical_question
 * scope i self-determines mapping flagged-H2 → suggested anchor.
 */
async function validateOutlineRule14b(
  h2Sections: Array<{ header: string; source_area?: string | null }>,
  primaryEntity: string,
  documentRole: string,
  canonicalQuestion: string,
  kgSubEntities: string[],
  eavAttributes: Array<{ entity_name: string; attribute_name: string; value: string }>,
  detectorResult: Rule14bDetectorResult,
  mapId: string,
  nodeId: string,
): Promise<Rule14bValidationResult> {
  if (!detectorResult.triggered || detectorResult.flagged_h2s.length === 0) {
    return {
      valid: true,
      detectorResult,
      judgeVerdict: "SKIPPED",
      flaggedAfterJudge: [],
      judgeCostUsd: 0,
    };
  }

  const kgLines = kgSubEntities.length > 0
    ? kgSubEntities.slice(0, 30).map((e) => `  - ${e}`).join("\n")
    : "  (brak)";

  const eavLines = eavAttributes.length > 0
    ? eavAttributes.slice(0, 15)
        .map((a) => `  - ${a.entity_name} | ${a.attribute_name}: ${a.value}`)
        .join("\n")
    : "  (brak)";

  const flaggedLines = detectorResult.flagged_h2s
    .map((f) => `- "${f.header}" (detector matched generic opener pattern: "${f.matched_pattern}")`)
    .join("\n");

  const allH2sLines = h2Sections
    .filter((h) => h.header)
    .map((h2, i) => `${i + 1}. ${h2.header}${h2.source_area ? `  [source_area: ${h2.source_area}]` : ""}`)
    .join("\n");

  // PLACEHOLDER — sro-advisor will provide actual judge prompt content.
  // Skeleton state: empty RULE_14B_OPENER_PATTERNS → detector never triggers → this
  // path is unreachable until sro-advisor populates patterns + this prompt.
  const judgePrompt = `[RULE_14B_JUDGE_PROMPT_PLACEHOLDER — sro-advisor to provide]

Kontekst:
- primary_entity: "${primaryEntity}"
- canonical_question: "${canonicalQuestion}"
- document_role: "${documentRole}"

Wszystkie H2 (kontekst cross-outline):
${allH2sLines}

Flagged H2 (detector triggered generic structural opener):
${flaggedLines}

KG sub-entities pool (flat, NIE pre-distributed per H2):
${kgLines}

EAV attributes pool (flat, top 15):
${eavLines}

Output JSON:
{ "verdict": "KOPIA"|"UNIKALNY",
  "judgments": [
    { "h2_title": "...",
      "verdict": "KOPIA"|"UNIKALNY",
      "suggested_anchor_source": "KG_entity"|"EAV_attribute"|"canonical_question_scope"|null,
      "suggested_anchor_value": "konkretna wartosc z pool lub null gdy UNIKALNY",
      "reason": "1-zdaniowy powod" } ] }`;

  const judgeProvider = getLLMProvider("openai");
  const judgeResponse = await judgeProvider.call(judgePrompt, {
    model: "gpt-4.1-mini",
    temperature: 0.1,
    jsonSchema: RULE_14B_JUDGE_SCHEMA,
    step: "outline_rule_14b_judge",
    mapId,
    nodeId,
  });

  let parsed: {
    verdict: string;
    judgments: Array<{
      h2_title: string;
      verdict: string;
      suggested_anchor_source: string | null;
      suggested_anchor_value: string | null;
      reason: string;
    }>;
  };
  try {
    parsed = parseLLMJSON(judgeResponse.text);
  } catch {
    logDebug("Outline", `Rule 14b judge: failed to parse, treating as valid`);
    return {
      valid: true,
      detectorResult,
      judgeVerdict: "SKIPPED",
      flaggedAfterJudge: [],
      judgeCostUsd: judgeResponse.costUsd || 0,
    };
  }

  const verdictNormalized: "KOPIA" | "UNIKALNY" =
    parsed.verdict === "UNIKALNY" ? "UNIKALNY" : "KOPIA";

  const flaggedAfterJudge: Rule14bJudgmentItem[] = (parsed.judgments || [])
    .filter((j) => j.verdict === "KOPIA")
    .map((j) => ({
      title: j.h2_title,
      suggested_anchor_source: j.suggested_anchor_source,
      suggested_anchor_value: j.suggested_anchor_value,
      reason: j.reason,
    }));

  return {
    valid: verdictNormalized === "UNIKALNY" && flaggedAfterJudge.length === 0,
    detectorResult,
    judgeVerdict: verdictNormalized,
    flaggedAfterJudge,
    judgeCostUsd: judgeResponse.costUsd || 0,
  };
}

// =============================================
// Rule 7.6 — Header Anchor Preservation (NEW v14, P7 fix)
// =============================================
// Detects when LLM rephrase strips ALL concrete anchors (KG entities / measurables /
// dates) from h2.header while source_area retains them.
// Bug empirycznie potwierdzony: ICE H2.4 source_area "Wagi 50% / 30% / 20%" →
// header "Wazone proporcje scoringu w duzych organizacjach" (utrata wszystkich
// measurables). Cross-mapa systemowy (3 nody tested: ICE / churn / psychologia).
//
// Anchor extraction: 5 regex categories (capitalized tokens / percentages / ratios /
// years / counts-with-units). Min length filter ≥4 chars (eliminuje 3-char false
// positives jak "Cel" / "ICE" / "PIE").
//
// PE exclusion (Korekta 2): primary_entity wykluczony z anchor pool — header NIE
// jest wymuszany do zachowania PE (Rule 14 by go flagowała jako stuffing).
//
// Token-level match (Korekta 1): multi-word anchor PASS gdy ≥1 significant token
// (≥3 chars) jest w header — relaxed substring match dla phrasing variants
// ("First Round 2015" → "First Round w 2015 r" → match przez "first"/"round"/"2015").

// v9 C5: Rule 15 LLM judge — replaces regex extractAnchorsFromSourceArea + substring check.
// Semantic equivalence cross-niche cross-language (gpt-4.1-mini, temp 0.1).
// Pattern 1:1 z Rule 14 judge (RULE14_JUDGE_SCHEMA + validateOutlineRule14).
interface Rule15ViolationDetail {
  header: string;
  source_area: string;
  anchor_pool_size: number;        // filtered pool size (post-PE-exclude)
  available_anchors: string[];     // filtered pool values (dla retry feedback)
  matched_anchor: string | null;
  reason: string;
}

interface Rule15ValidationResult {
  valid: boolean;
  violations: Rule15ViolationDetail[];
  judgeCostUsd: number;
  judgeAttempts: number;
}

const RULE15_JUDGE_SCHEMA = {
  type: "object" as const,
  properties: {
    judgments: {
      type: "array" as const,
      items: {
        type: "object" as const,
        properties: {
          h2_title: { type: "string" as const },
          preserves_anchor: { type: "boolean" as const },
          matched_anchor: { type: "string" as const }, // "" gdy preserves_anchor=false
          reason: { type: "string" as const },
        },
        required: ["h2_title", "preserves_anchor", "matched_anchor", "reason"] as const,
        additionalProperties: false as const,
      },
    },
  },
  required: ["judgments"] as const,
  additionalProperties: false as const,
};

// v9 C3: per-node EAV builder. Eliminuje map-wide bleed z ctxEav (15 random EAV-ow z calej
// mapy w eav_context outline prompt). 3-layer per-node first scope:
//   Layer 1: content_knowledge_graphs.kg_data.measurables per node_id (top 5)
//   Layer 2: content_knowledge_graphs.kg_data.facts per node_id, priority='high' AND confidence>=0.7 (top 4)
//   Layer 3: thematic_entity_attributes per primary_entity_id (NIE map_id), exclude metadata (top 6)
// KB SRO §4.3: supporting entities "disambiguate" = per-node, NIE map-wide rozpraszanie.
// Hard cap 12 entries → outline prompt context budget.
async function buildPerNodeEavForOutline(
  nodeId: string,
  primaryEntity: string,
): Promise<Array<{ entity_name: string; attribute_name: string; value: string }>> {
  const eavList: Array<{ entity_name: string; attribute_name: string; value: string }> = [];

  // Layer 1+2: kg_data per-node (measurables + high-priority facts)
  const { data: kgRow } = await (supabase as any)
    .from("content_knowledge_graphs")
    .select("kg_data")
    .eq("node_id", nodeId)
    .maybeSingle();

  if (kgRow?.kg_data) {
    // Measurables → entity_name=PE, attribute_name=measurable, value=value+unit
    for (const m of (kgRow.kg_data.measurables || []).slice(0, 5)) {
      const valStr = `${m.value || ""}${m.unit ? " " + m.unit : ""}`.trim();
      if (m.measurable && valStr) {
        eavList.push({
          entity_name: primaryEntity || "(brak PE)",
          attribute_name: String(m.measurable),
          value: valStr,
        });
      }
    }
    // High-priority + high-confidence facts (binary AND check)
    const facts = (kgRow.kg_data.facts || [])
      .filter((f: any) => f.priority === "high" && (f.confidence || 0) >= 0.7)
      .slice(0, 4);
    for (const f of facts) {
      if (f.fact) {
        eavList.push({
          entity_name: primaryEntity || "(brak PE)",
          attribute_name: "fact",
          value: String(f.fact),
        });
      }
    }
  }

  // Layer 3: thematic_entity_attributes per primary_entity_id (NIE map_id leakage)
  // Lookup id via thematic_nodes.primary_entity_id (same pattern jak queryFanOut.fetchEavPerPrimaryEntityFallback)
  const { data: nodeRow } = await (supabase as any)
    .from("thematic_nodes")
    .select("primary_entity_id")
    .eq("id", nodeId)
    .maybeSingle();

  if (nodeRow?.primary_entity_id) {
    const { data: attrRows } = await (supabase as any)
      .from("thematic_entity_attributes")
      .select("attribute_name, value")
      .eq("entity_id", nodeRow.primary_entity_id)
      .not("attribute_name", "in", '("reading_time","word_count","meta_description")')
      .limit(6);
    for (const a of (attrRows || [])) {
      if (a.attribute_name && a.value != null) {
        eavList.push({
          entity_name: primaryEntity || "(brak PE)",
          attribute_name: String(a.attribute_name),
          value: String(a.value),
        });
      }
    }
  }

  return eavList.slice(0, 12);
}

// v9 C2: source_area sanitize helper (extracted z post-retry for-loop, linia 1290-1324).
// Idempotent: re-run na sanitized data = no-op. Mutates section.source_area in place.
// Wywoływany 2x: (a) w retry loop PRZED walidator (Rule 14/14b/15) — żeby walidator
// widział finalny dataset, NIE pre-normalize hallucynacje; (b) post-retry na fresh parse
// (outlineData.sections to inna referencja niż sectionsForJudge bo re-parse outlineResponse.text).
function sanitizeOutlineSourceArea(
  sections: Array<{ source_area?: string | null; type?: string }>,
  inputAreaNameByNorm: Map<string, string>,
  normalizeAreaName: (s: string) => string,
): { invalidAreaCount: number; normalizedCount: number } {
  let invalidAreaCount = 0;
  let normalizedCount = 0;
  for (const section of sections) {
    if (section.type !== "h2" || !section.source_area) continue;
    // Normalize trailing PAA suffix + leading/trailing quotes (extended quote class PL/DE/JP)
    const raw = section.source_area;
    let normalized = raw.replace(/\s*:\s*["'“”„][^"'“”„]*["'“”„]?\s*$/, "");
    normalized = normalized.replace(/^["'`“”„\s]+|["'`“”„\s]+$/g, "").trim();
    if (normalized !== raw) {
      section.source_area = normalized;
      normalizedCount++;
    }
    // Membership check vs input
    const normLlm = normalizeAreaName(section.source_area || "");
    const origInputName = inputAreaNameByNorm.get(normLlm);
    if (!origInputName) {
      invalidAreaCount++;
      section.source_area = ""; // halucynacja → zerowanie (walidator zobaczy)
    } else if (origInputName !== section.source_area) {
      section.source_area = origInputName; // restore z input (LLM zgubil tag)
    }
  }
  return { invalidAreaCount, normalizedCount };
}

/**
 * Rule 15 runtime guard — LLM-judge validation (v9 C5, replaces regex extractor pre-C5).
 *
 * Druga LLM (cheap call, gpt-4.1-mini) ocenia per H2 czy header zachowuje konkret
 * z anchor_pool tej sekcji — substring verbatim LUB semantic equivalent. Cross-niche
 * cross-language (PL/EN/DE/JP/medical/legal/financial/culinary).
 *
 * Pattern 1:1 z validateOutlineRule14 (KB §11 Closed Loop Enforcement):
 *  - gpt-4.1-mini, temperature=0.1, jsonSchema strict
 *  - PE filter pre-LLM (oszczędność tokenów + skip judge call gdy filtered pool empty)
 *  - Soft fallback on parse fail: return { valid: true, ... }
 *  - Forensics: judgeCostUsd + judgeAttempts persisted w engine_debug.outline.rule_15
 *
 * KB anchors: §4.2 verifiable detail binary, §2.5 Entity Anchoring (PE excluded), §11
 * validator enforce (NIE silent skip jak pre-C5 regex 3 false PASS gates).
 *
 * Judge prompt VERBATIM dostarczony przez sro-advisor (KB-aligned cross-niche
 * MATCH/NO MATCH examples). NIE parafrazować bez ack — każdy example jest precyzyjnie
 * dobrany pod §4.2 + §2.5.
 */
async function validateOutlineRule15(
  h2Sections: Array<{ header: string; source_area?: string | null }>,
  primaryEntity: string,
  microAreas: MicroArea[],
  mapId: string,
  nodeId: string,
): Promise<Rule15ValidationResult> {
  // Build source_area → anchor_pool lookup z microAreas (Q1 answer: detail
  // implementacyjny judge'a, NIE odpowiedzialność callera)
  const areaToPool = new Map<string, AnchorCandidate[]>();
  for (const m of microAreas) {
    if (m.area) areaToPool.set(m.area, m.anchor_pool || []);
  }

  // PE token-set dla pre-LLM filter (Q2 answer: code-side filter, oszczędność
  // tokenów + skip judge gdy pool empty post-PE-exclude → EXCEPTION shortcut)
  const peLower = (primaryEntity || "").toLowerCase().trim();
  const peTokens = new Set(peLower.split(/\s+/).filter((t) => t.length >= 3));

  // Per-H2 candidate prep: filter pool PE-tokens, EXCEPTION clause gdy empty
  type Candidate = { header: string; source_area: string; pool: string[] };
  const candidates: Candidate[] = [];
  const exceptions: Rule15ViolationDetail[] = []; // tracked dla forensics, NIE violations

  for (const h2 of h2Sections) {
    if (!h2.header || !h2.source_area) continue; // post-C2 puste = halucynacja, ale source_area="" → skip (judge nie ma co oceniać bez source_area mapping)
    const fullPool = areaToPool.get(h2.source_area) || [];
    if (fullPool.length === 0) continue; // defensive: source_area nie matche żadnego micro_area (post-C2 sanitize gwarantuje, ale fallback)

    // KB §2.5: exclude anchors których WSZYSTKIE tokens są PE tokens (PE-only)
    const filteredValues = fullPool
      .map((a) => a.value)
      .filter((v) => {
        const tokens = v.toLowerCase().split(/\s+/).filter((t) => t.length >= 3);
        if (tokens.length === 0 || peTokens.size === 0) return true;
        return !tokens.every((t) => peTokens.has(t));
      });

    if (filteredValues.length === 0) {
      // EXCEPTION clause (Q2 answer): pool empty post-PE-exclude → skip judge call,
      // mark as "PE-only legitimate" (typu DIRECT_ANSWER definicja). NIE violation.
      logDebug("Outline", `Rule 15 EXCEPTION: H2 "${h2.header}" anchor_pool empty post-PE-exclude → skip (DIRECT_ANSWER-like)`);
      continue;
    }
    candidates.push({ header: h2.header, source_area: h2.source_area, pool: filteredValues });
  }

  if (candidates.length === 0) {
    // Wszystkie H2 trivially valid (no source_area / no pool / PE-only EXCEPTION)
    return { valid: true, violations: [], judgeCostUsd: 0, judgeAttempts: 0 };
  }

  // VERBATIM judge prompt — KB §4.2 + §2.5 aligned, cross-niche illustrative examples
  // (growth hacking + culinary + medical + legal + financial). ZERO numerical thresholds,
  // ZERO banned phrases, ZERO hardcoded niche. Sro-advisor finalized — NIE parafrazować.
  const numberedH2s = candidates
    .map((c, i) => `\n${i + 1}. HEADER: "${c.header}"\n   ANCHORS (header MUSI zachowac min 1):\n${c.pool.map((v) => `   - "${v}"`).join("\n")}`)
    .join("\n");

  const judgePrompt = `Ocen czy KAŻDY header H2 zachowuje konkret z listy ANCHORS tej sekcji.

Reguła SRO (KB §4.2 + §2.5): header sekcji MUSI zawierać verifiable detail z anchor pool — substring verbatim LUB semantic equivalent. Konkret = nazwana encja / measurable value / data / aktor / fakt. Generic rephrase który gubi wszystkie konkrety = REJECT.

PRIMARY_ENTITY (wyłączony z anchor pool — NIE liczy się jako anchor): "${primaryEntity}"

H2 do oceny:
${numberedH2s}

DEFINICJA "zachowany konkret":

✓ MATCH (substring verbatim lub semantic equivalent):
- ANCHOR "5-25 razy" ↔ HEADER "5x do 25x" → MATCH (numeric paraphrase)
- ANCHOR "5-25 razy" ↔ HEADER "pięciokrotnie do dwudziestopięciokrotnie" → MATCH (numeric verbal equivalent)
- ANCHOR "Sean Ellis 2010" ↔ HEADER "narodziny w 2010 wg Ellisa" → MATCH (rearranged tokens, semantic preserved)
- ANCHOR "Customer Retention" ↔ HEADER "utrzymanie klienta" → MATCH (PL/EN translation, semantic preserved)
- ANCHOR "art. 415 KC" ↔ HEADER "odpowiedzialność z art. 415" → MATCH (legal ref preserved)
- ANCHOR "5-25 µg/dL" ↔ HEADER "norma 5-25 mikrogram na decylitr" → MATCH (unit verbal equivalent)
- ANCHOR "Booking.com" ↔ HEADER "Booking.com i Amazon: testy A/B" → MATCH (verbatim substring)
- ANCHOR "300 mln dolarów" ↔ HEADER "$300M wzrostu revenue" → MATCH (currency notation equivalent)
- ANCHOR "Lactobacillus brevis" ↔ HEADER "kultury L. brevis w fermentacji" → MATCH (taxonomic abbreviation)

✗ NO MATCH (abstract rephrase gubi konkrety):
- ANCHORS ["5 %", "500 klientów", "25 utraconymi"] ↔ HEADER "Przykład miesięcznego wskaźnika utraty klientów" → NO MATCH (wszystkie liczby zgubione)
- ANCHORS ["Customer Retention", "CAC", "5-25 razy"] ↔ HEADER "Koszt pozyskania klienta a strategie retencji" → NO MATCH (CAC zgubione, 5-25 zgubione, "retencja" to PE-paraphrase, NIE Customer Retention)
- ANCHORS ["First Round", "2015", "kanoniczny opis"] ↔ HEADER "Geneza i kanoniczna definicja frameworku" → NO MATCH (First Round zgubione, 2015 zgubione)
- ANCHORS ["Cialdini", "6 zasad", "1984"] ↔ HEADER "Klasyczne zasady psychologii wpływu" → NO MATCH (Cialdini zgubione, 6 zgubione, 1984 zgubione)

WAŻNE — zasada PE exclusion:
Jeśli header zawiera TYLKO primary_entity (np. "Cechy churn", "Geneza ICE") i ŻADEN inny konkret z anchor_pool → preserves_anchor=false. PE jako dekoracja NIE liczy się jako anchor.

Wyjątek: H2 typu DIRECT_ANSWER definiujący samą encję ("Czym jest X", "Definicja X") — PE jest legitymny topical anchor. Walidator wykryje ten case wcześniej przez EXCEPTION clause (filtered pool empty po PE-removal) i NIE prześle do oceny. Jeśli widzisz taki case w batchu → preserves_anchor=true.

Output JSON (strict schema):
{
  "judgments": [
    {
      "h2_title": "exact header text",
      "preserves_anchor": true|false,
      "matched_anchor": "value z ANCHORS który header zachował" lub "" gdy preserves_anchor=false,
      "reason": "1-zdaniowe uzasadnienie (po polsku)"
    }
  ]
}`;

  const judgeProvider = getLLMProvider("openai");
  let judgeResponse;
  try {
    judgeResponse = await judgeProvider.call(judgePrompt, {
      model: "gpt-4.1-mini",
      temperature: 0.1,
      jsonSchema: RULE15_JUDGE_SCHEMA,
      step: "outline_rule_15_judge",
      mapId,
      nodeId,
    });
  } catch (e) {
    logDebug("Outline", `Rule 15 judge call failed: ${e instanceof Error ? e.message : String(e)}, treating as valid (soft fallback)`);
    return { valid: true, violations: [], judgeCostUsd: 0, judgeAttempts: 1 };
  }

  let parsed: { judgments: Array<{ h2_title: string; preserves_anchor: boolean; matched_anchor: string; reason: string }> };
  try {
    parsed = parseLLMJSON(judgeResponse.text);
  } catch {
    logDebug("Outline", "Rule 15 judge parse fail, treating as valid (soft fallback)");
    return { valid: true, violations: [], judgeCostUsd: judgeResponse.costUsd || 0, judgeAttempts: 1 };
  }

  const violations: Rule15ViolationDetail[] = (parsed.judgments || [])
    .filter((j) => j && !j.preserves_anchor)
    .map((j) => {
      const c = candidates.find((x) => x.header === j.h2_title);
      return {
        header: j.h2_title,
        source_area: c?.source_area || "",
        anchor_pool_size: c?.pool.length || 0,
        available_anchors: c?.pool || [],
        matched_anchor: j.matched_anchor || null,
        reason: j.reason || "",
      };
    });

  return {
    valid: violations.length === 0,
    violations,
    judgeCostUsd: judgeResponse.costUsd || 0,
    judgeAttempts: 1,
  };
}

/**
 * Generate outline + distribute KG data to sections.
 * Two LLM calls: (1) outline, (2) distribution.
 */
export interface OutlineContext {
  document_role: string;
  primary_entity: string;
  canonical_question: string;
  validated_question?: string | null;
  best_market_keyword?: string | null;
  market_keywords_top5?: Array<{ keyword: string; search_volume: number }>;
  boundaries_not_owned?: string[];
}

export async function generateOutline(
  nodeId: string,
  mapId: string,
  fanOut: FanOut,
  kg: KnowledgeGraph,
  seoTitle: string,
  language: string,
  outlineCtx?: OutlineContext,
  ctxEav?: Array<{ entity_name: string; attribute_name: string; value: string }>,
  // Mapping lowercase canonical_name → thematic_entities.id (UUID), z Micro-EAV.
  // Uzywane do enrichowania distribution.entity_db_ids — downstream moze siegnac DB.
  entityNameToDbId?: Record<string, string>,
  // STOP-C': options bag dla pillar flow + extra template vars (A1 z planu)
  // Backward compat: undefined → standard 'outline_generation' prompt, no extra vars.
  outlineOptions?: {
    promptKey?: string;
    extraVars?: Record<string, string>;
  },
): Promise<{ outline: Outline; costUsd: number; debug: Record<string, unknown> }> {
  let totalCost = 0;

  // Load prompt from DB — uzyj promptKey override jesli podany (np. 'outline_generation_pillar')
  const dbPrompt = await loadPrompt((outlineOptions?.promptKey ?? "outline_generation") as any);
  const stageConfig = getStageConfig("outline");

  // ---- PART A: Outline Generation ----

  // Sanitize fanOut — LLM may return incomplete structure
  const microAreas = fanOut.micro_areas || [];
  const defAreas = fanOut.definitional_areas || [];
  const primaryIntentType = fanOut.primary_intent?.type || "Definicyjna";

  // Log full fan-out input
  logPublic("Outline", `Outline input: ${microAreas.length} micro areas, ${defAreas.length} def areas, primary=${primaryIntentType}`);
  logDebug("Outline", "Fan-Out input details", {
    primary_intent: primaryIntentType,
    micro_areas_count: microAreas.length,
    def_areas_count: defAreas.length,
    total_paa: microAreas.reduce((s, m) => s + (m.paa_questions?.length || 0), 0),
    top_level_paa: (fanOut.paa_questions || []).length,
  });
  // Per-area breakdown
  for (const m of microAreas) {
    logDebug("Outline", `area: [${m.intent}] "${m.area}" | question: "${m.question}" | PAA: ${(m.paa_questions || []).length}${(m.paa_questions || []).length > 0 ? ` → [${m.paa_questions.join("; ")}]` : ""}`);
  }

  // Build paaToOriginTag map (G11 anti-misalignment): kazdy PAA ma origin SRO tag
  // pochodzacy z area, do ktorej PAA byl pierwotnie przypisany w fan_out/consolidation.
  // Po LLM outline generation sprawdzimy w G11 czy h3.source_paa pasuje do parent H2 SRO tag.
  // Mismatch (np. H2 [DIRECT_ANSWER] z H3 source_paa pochodzacym z area [SYMPTOMS]) = drop.
  const paaToOriginTag = new Map<string, string>();
  for (const m of microAreas) {
    const tag = m.area.match(/\[([A-Z_]+)\]/)?.[1];
    if (!tag) continue;
    for (const paa of m.paa_questions || []) {
      paaToOriginTag.set(normalizeForDedup(paa), tag);
    }
  }

  // Sort micro_areas: primary intent first, then secondary, then definitional
  const primaryAreas = microAreas.filter(
    (m) => m.intent === primaryIntentType,
  );
  const secondaryAreas = microAreas.filter(
    (m) => m.intent !== primaryIntentType,
  );

  logDebug("Outline", `Split: ${primaryAreas.length} primary (${primaryIntentType}) + ${secondaryAreas.length} secondary`);

  // Format: area.name zaczyna sie od SRO tagu [STEPS]/[CAUSES]/..., intent jako
  // adnotacja na koncu (nie prefix). Zapobiega kopiowaniu [Intent] do source_area
  // przez LLM — walidator inputAreaNameByNorm expectuje "[SRO_TAG] Nazwa".
  const areasJson = microAreas
    .map((m) => `- ${m.area}: "${m.question}" (PAA: ${(m.paa_questions || []).length})  [intent: ${m.intent}]`)
    .join("\n");

  const defAreasJson = defAreas
    .map((d) => `- ${d.term}: ${d.expected_content}`)
    .join("\n");

  // Deduplicate PAA: sanitize junk + dedup vs area question AND vs H1
  const h1Norm = normalizeForDedup(seoTitle);
  const mainQNorm = normalizeForDedup(outlineCtx?.canonical_question || seoTitle);

  const primaryAreasStr = primaryAreas
    .map((m) => {
      const areaNorm = normalizeForDedup(m.question || m.area);
      const rawPaa = m.paa_questions || [];

      // BUG B FIX: Filter junk PAA values before dedup
      const sanitizedPaa = rawPaa.filter((paa) => {
        if (!paa || paa.trim().length < 5) return false;
        if (/^\d+$/.test(paa.trim())) return false;
        if (paa.trim().toLowerCase().startsWith("paa:")) return false;
        return true;
      });
      const junkRemoved = rawPaa.length - sanitizedPaa.length;
      if (junkRemoved > 0) {
        logIntegrity("paa_sanitation", false, { area: m.area, junk_removed: junkRemoved, kept: sanitizedPaa.length });
      }

      const uniquePaa = sanitizedPaa.filter((paa) => {
        const paaNorm = normalizeForDedup(paa);
        // PAA duplicate of area question
        const isDupArea = paaNorm === areaNorm || paaNorm.includes(areaNorm) || areaNorm.includes(paaNorm);
        // PAA duplicate of H1 / main question
        const isDupH1 = paaNorm === h1Norm || paaNorm.includes(h1Norm) || h1Norm.includes(paaNorm)
          || paaNorm === mainQNorm || paaNorm.includes(mainQNorm) || mainQNorm.includes(paaNorm);
        if (isDupArea) logDebug("Outline", `PAA dedup: removed "${paa}" (duplicate of area "${m.question}")`);
        if (isDupH1) logDebug("Outline", `PAA dedup: removed "${paa}" (duplicate of H1 "${seoTitle}")`);
        return !isDupArea && !isDupH1;
      });
      if (rawPaa.length > 0) {
        logDebug("Outline", `Area "${m.area}": ${rawPaa.length} raw PAA → ${uniquePaa.length} unique after dedup`);
      }

      // v9 C4: ANCHORS rendering per area (value-only, no source/type labels).
      // anchor_pool: AnchorCandidate[] z Fan-Out v9 C1 (ALWAYS min 1 element guarded
      // przez buildAnchorPool PE-verbatim final guard). Multi-line format dla
      // readability + clear separation between Q/PAA/ANCHORS. v15 prompt Rule 7.6
      // binary check: header MUSI zachowac konkret z ANCHORS (substring lub semantic
      // equivalent). LLM widzi value tylko — NIE source label, NIE priority hint.
      const anchorsBlock = (m.anchor_pool && m.anchor_pool.length > 0)
        ? `\n  ANCHORS:\n${m.anchor_pool.map((a) => `    - "${a.value}"`).join("\n")}`
        : "";
      const paaBlock = uniquePaa.length > 0 ? `\n  PAA: ${uniquePaa.join("; ")}` : "";
      return `- ${m.area}\n  Q: "${m.question}"${paaBlock}${anchorsBlock}`;
    })
    .join("\n\n");

  // Sekcje kontekstowe — ten sam format co areasJson: intent jako adnotacja,
  // SRO tag na poczatku area.name. Bez tego LLM kopiowal [Intent] [SRO_TAG]
  // jako source_area → walidator nie matchowal → halucynacje.
  const secondaryAreasStr = secondaryAreas
    .map((m) => `- ${m.area}: "${m.question}"  [intent: ${m.intent}]`)
    .join("\n") || "";

  logDebug("Outline", `primaryAreasStr (what LLM sees):\n${primaryAreasStr}`);
  if (secondaryAreasStr) logDebug("Outline", `secondaryAreasStr:\n${secondaryAreasStr}`);

  // Role-specific structural hints (C5)
  const ROLE_HINTS: Record<string, string> = {
    definition: "Struktura: intro + definicja + cechy/atrybuty + zastosowania + powiazane pojecia",
    glossary: "Struktura: intro + definicja + kontekst + przyklady",
    how_to: "Struktura: intro + krok po kroku + materialy/narzedzia + typowe bledy + wskazowki",
    protocol: "Struktura: intro + zasady + procedura + wyjatki",
    guide: "Struktura: intro + podstawy + szczegoly + zaawansowane + FAQ",
    comparison: "Struktura: intro + tabela porownawcza + kluczowe roznice + kiedy co wybrac",
    troubleshooting: "Struktura: intro + objawy + przyczyny + rozwiazania krok po kroku",
    history: "Struktura: intro + poczatki/geneza + ewolucja + stan obecny + znaczenie",
    commercial: "Struktura: intro + cechy produktu + porownanie opcji + gdzie kupic/cena",
  };
  const roleHint = outlineCtx?.document_role ? ROLE_HINTS[outlineCtx.document_role] || "" : "";

  // Market keywords — filter by scope relevance (not just entity match)
  const canonNorm = normalizeForDedup(outlineCtx?.canonical_question || seoTitle);
  const validNorm = outlineCtx?.validated_question ? normalizeForDedup(outlineCtx.validated_question) : "";
  const entityNorm = normalizeForDedup(outlineCtx?.primary_entity || "");
  const role = outlineCtx?.document_role || "unknown";
  const isDefRole = role === "definition" || role === "glossary";

  /** Check if market keyword is scope-relevant. Returns { pass, reason } */
  function checkMarketKwRelevance(kw: string): { pass: boolean; reason: string } {
    const kwNorm = normalizeForDedup(kw);
    if (!kwNorm || kwNorm.length < 3) return { pass: false, reason: "too_short" };
    if (canonNorm && isNearExact(kwNorm, canonNorm)) return { pass: true, reason: "near_exact_canonical" };
    if (validNorm && isNearExact(kwNorm, validNorm)) return { pass: true, reason: "near_exact_validated" };
    if (!isDefRole && entityNorm && (kwNorm === entityNorm || isNearExact(kwNorm, entityNorm)))
      return { pass: false, reason: "bare_entity_for_non_def_role" };
    return { pass: false, reason: "no_scope_match" };
  }

  /** Token-subset check with wrapper-only extra tokens (same logic as reverseSerpValidator) */
  function isNearExact(a: string, b: string): boolean {
    const ta = a.split(/\s+/).filter(Boolean);
    const tb = b.split(/\s+/).filter(Boolean);
    if (ta.length === 0 || tb.length === 0) return false;
    if (ta.join(" ") === tb.join(" ")) return true;
    const WRAPPERS = new Set(["co", "to", "jest", "czym", "czy", "na", "do", "w", "z", "i", "oraz", "a"]);
    const [shorter, longer] = ta.length <= tb.length ? [ta, tb] : [tb, ta];
    const longerSet = new Set(longer);
    if (!shorter.every((w) => longerSet.has(w))) return false;
    const shorterSet = new Set(shorter);
    return longer.filter((w) => !shorterSet.has(w)).every((w) => WRAPPERS.has(w));
  }

  // Filter best_market_keyword
  const bestKwCheck = outlineCtx?.best_market_keyword ? checkMarketKwRelevance(outlineCtx.best_market_keyword) : null;
  const filteredBestKw = (bestKwCheck?.pass) ? outlineCtx!.best_market_keyword : "";

  // Filter market_keywords_top5
  const rawMarketKws = outlineCtx?.market_keywords_top5 || [];
  const filteredMarketKws: typeof rawMarketKws = [];
  const rejectedMarketKws: Array<{ keyword: string; reason: string }> = [];
  for (const k of rawMarketKws) {
    const check = checkMarketKwRelevance(k.keyword);
    if (check.pass) {
      filteredMarketKws.push(k);
    } else {
      rejectedMarketKws.push({ keyword: k.keyword, reason: check.reason });
    }
  }

  // Detailed log
  logDebug("Outline", "Market keyword filter", {
    document_role: role,
    canonical_question: outlineCtx?.canonical_question || seoTitle,
    validated_question: outlineCtx?.validated_question || "(none)",
    best_market_keyword_before: outlineCtx?.best_market_keyword || "(none)",
    best_market_keyword_after: filteredBestKw || "(removed)",
    best_market_keyword_reason: bestKwCheck?.reason || "n/a",
    market_keywords_before: rawMarketKws.map((k) => k.keyword),
    market_keywords_after: filteredMarketKws.map((k) => k.keyword),
    rejected: rejectedMarketKws,
  });

  const marketKwStr = filteredMarketKws.length > 0
    ? filteredMarketKws.map((k) => `"${k.keyword}" (SV=${k.search_volume})`).join(", ")
    : "";

  // Boundaries constraint
  const boundariesStr = (outlineCtx?.boundaries_not_owned || []).slice(0, 10).join(", ");

  // v13: Profile-driven style/structure decisions (writing_profile_* vars)
  // Inline fetch — outlineCtx nie zawiera profile, a NodeContentContext.writingProfile
  // ma tylko 3 z 7 potrzebnych pol (brak: use_tables, required_sections, h2_patterns, core_entities).
  // Profile null -> wszystkie wartosci default (empty/neutral).
  const { data: profileRow } = await (supabase as any)
    .from("thematic_writing_profiles")
    .select("use_tables, required_sections, h2_patterns, core_entities, evidence_style, claim_strength, language_rules")
    .eq("map_id", mapId)
    .eq("is_active", true)
    .maybeSingle();

  const writingProfileVars: Record<string, string> = {
    writing_profile_use_tables: profileRow?.use_tables ?? "when_useful",
    writing_profile_required_sections: JSON.stringify(profileRow?.required_sections ?? []),
    writing_profile_h2_patterns: JSON.stringify(profileRow?.h2_patterns ?? {}),
    writing_profile_core_entities: JSON.stringify(profileRow?.core_entities ?? []),
    writing_profile_evidence_style: profileRow?.evidence_style ?? "",
    writing_profile_claim_strength: profileRow?.claim_strength ?? "moderate",
    writing_profile_language_rules: profileRow?.language_rules ?? "",
  };

  const outlineTemplateVars: Record<string, string> = {
    seo_title: seoTitle,
    keyword: seoTitle,
    h1_title: seoTitle,
    language,
    primary_intent: primaryIntentType,
    primary_intent_type: primaryIntentType,
    primary_areas: primaryAreasStr,
    secondary_areas: secondaryAreasStr,
    definitional_areas: defAreasJson || "",
    fan_out_data: areasJson || "",
    paa_data: microAreas.flatMap((m) => m.paa_questions || []).join("\n") || "",
    // C3: new context variables
    document_role: role,
    primary_entity: outlineCtx?.primary_entity || "",
    canonical_question: outlineCtx?.canonical_question || seoTitle,
    validated_question: outlineCtx?.validated_question || "",
    best_market_keyword: filteredBestKw,
    market_keywords: marketKwStr,
    boundaries_not_owned: boundariesStr,
    role_hint: roleHint,
    // v9 C3: per-node EAV (eliminate map-wide bleed). Wczesniej (pre-C3) eav_context
    // wystawial 15 random EAV-ow z calej mapy (ctxEav slice(0,15)), prowadzac do
    // cross-node leakage (Amazon $300M z psychologia w prompcie churn). Teraz per-node
    // first scope: kg_data.measurables + high-priority facts + thematic_entity_attributes
    // by primary_entity_id (NIE map_id). KB SRO §4.3.
    eav_context: "(buildPerNodeEavForOutline pending — async, populated below)",
    ...writingProfileVars,
    // STOP-C': extra vars (np. cluster_member_entities, cluster_member_slugs dla pillar flow)
    ...(outlineOptions?.extraVars ?? {}),
  };

  // Async per-node EAV fetch + override placeholder PRZED fillTemplate
  const perNodeEav = await buildPerNodeEavForOutline(nodeId, outlineCtx?.primary_entity || "");
  outlineTemplateVars.eav_context = perNodeEav.length === 0
    ? "(brak per-node EAV)"
    : perNodeEav.map((a) => `- ${a.entity_name} | ${a.attribute_name}: ${a.value}`).join("\n");
  logDebug("Outline", `Per-node EAV: ${perNodeEav.length} entries (pre-C3 was up to 15 map-wide)`);

  const outlinePrompt = dbPrompt
    ? fillTemplate(dbPrompt.template, outlineTemplateVars)
    : `Jesteś ekspertem SEO i strukturyzacji treści. Wygeneruj outline artykułu.

## Kontekst node'a
- Główne pytanie: "${outlineCtx?.canonical_question || seoTitle}"
${outlineCtx?.validated_question ? `- Zwalidowane pytanie (SERP): "${outlineCtx.validated_question}"` : ""}
- Główna encja: "${outlineCtx?.primary_entity || ""}"
- Rola dokumentu: ${outlineCtx?.document_role || "unknown"}
${roleHint ? `- Sugerowana struktura: ${roleHint}` : ""}
${outlineCtx?.best_market_keyword ? `- Główna fraza rynkowa: "${outlineCtx.best_market_keyword}"` : ""}
${marketKwStr ? `- Frazy rynkowe: ${marketKwStr}` : ""}

## Tytuł H1
${seoTitle}

## Język
${language}

## Mikro-obszary (z fan-out)
### Primary intent (${fanOut.primary_intent.type}):
${primaryAreasStr}

### Secondary intents:
${secondaryAreasStr}

### Definitional areas:
${defAreasJson || ""}

## Zasady outline
1. **Intro** (order=0): Krótki wstęp (2-3 zdania), odpowiada na główne pytanie. BEZ definicji.
2. **Primary intent areas** → pełne sekcje H2 (section_type="full") z H3:
   - Każdy primary area = 1 H2
   - Każde PAA pytanie = 1 H3 pod odpowiednim H2
   - H3 format: "question" jeśli z PAA, "context" jeśli kontekstowy
${secondaryAreas.length > 0 ? `3. **Secondary areas** → zgrupowane w 1-2 sekcje kontekstowe (section_type="context"), bez H3` : `3. **NIE generuj context sections** — INPUT nie zawiera secondary areas. Zwroc tylko intro + primary H2.`}
${defAreas.length > 0 ? `4. **Definitional areas** → 1 sekcja kontekstowa NA KOŃCU (przed podsumowaniem)` : ""}
5. **Sentence case** dla wszystkich nagłówków
6. Logiczna kolejność: od fundamentalnych do zaawansowanych
7. **KAŻDY H2 musi dotyczyć "${outlineCtx?.primary_entity || seoTitle}"** — nie odchodzić od głównej encji
8. **Rola dokumentu:** ${outlineCtx?.document_role || "standard"} — struktura H2 musi pasować do roli
${boundariesStr ? `9. **NIE pisz o tych tematach (poza scope):** ${boundariesStr}` : ""}`;

  const outlineModel = dbPrompt?.modelId || stageConfig.model;
  const outlineProviderName = dbPrompt?.provider || stageConfig.provider;

  logStage("OUTLINE", "START", { model: outlineModel, provider: outlineProviderName, primary_areas: primaryAreas.length, secondary_areas: secondaryAreas.length, document_role: role });
  logDebug("Outline", "LLM call config", {
    step: "outline_generation", provider: outlineProviderName, model: outlineModel,
    template_loaded: !!dbPrompt, template_version: (dbPrompt as any)?.version || "fallback",
    prompt_length: outlinePrompt.length,
  });
  logDebug("Outline", "LLM call context", {
    document_role: outlineCtx?.document_role || "none",
    primary_entity: outlineCtx?.primary_entity || "none",
    canonical_question: outlineCtx?.canonical_question || "none",
    validated_question: outlineCtx?.validated_question || "none",
    best_market_kw_filtered: filteredBestKw || "(removed/empty)",
    market_kw_filtered: marketKwStr || "(empty)",
    boundaries: boundariesStr || "(none)",
    role_hint: roleHint || "none",
  });
  logVerbose("Outline", `FULL PROMPT:\n${"─".repeat(60)}\n${outlinePrompt}\n${"─".repeat(60)}`);

  // ---- Rule 14 retry loop (v11) — semantic-judgment LLM judge guard ----
  // Max 2 retries z feedback prompt listujacym flagged H2s + judge reasons.
  // Soft fallback po retry exhaust (forensics flag, NIE blokuj pipeline'u).
  const MAX_OUTLINE_RETRIES = 2;
  const subEntities = (kg.entities || [])
    .map((e) => e.entity_name)
    .filter((name) => name && name.toLowerCase().trim() !== (outlineCtx?.primary_entity || "").toLowerCase().trim());

  // v9 C2: pre-build inputAreaNameByNorm + normalizeAreaName PRZED retry loop, żeby
  // walidatory Rule 14/14b/15 widziały sanitized source_area zamiast pre-normalize LLM
  // output. Wczesniej (pre-C2) sanitize dzial sie POST-retry → walidator widzial inny
  // dataset niż DB. Same logic co post-retry for-loop (linia ~1280+), ekstraktowana
  // żeby uzywac z 2 miejsc (in-loop + post-loop idempotent safety net).
  const VALID_INTENTS = new Set([
    "Definicyjna", "Problemowa", "Instrukcyjna",
    "Decyzyjna", "Diagnostyczna", "Porownawcza",
  ]);
  const inputAreaNames = new Set<string>();
  for (const a of primaryAreas) inputAreaNames.add(a.area);
  for (const a of secondaryAreas) inputAreaNames.add(a.area);
  for (const a of defAreas) inputAreaNames.add(a.term);
  const INTENT_PREFIX_RE =
    /^\s*\[(Definicyjna|Problemowa|Instrukcyjna|Decyzyjna|Diagnostyczna|Porownawcza)\]\s*/i;
  const normalizeAreaName = (s: string) =>
    s.replace(INTENT_PREFIX_RE, "")
      .toLowerCase()
      .trim()
      .replace(/\s*\[eav\]\s*$/i, "")
      .replace(/\s+/g, " ");
  const inputAreaNameByNorm = new Map<string, string>();
  for (const a of inputAreaNames) {
    inputAreaNameByNorm.set(normalizeAreaName(a), a);
  }

  const outlineStart = Date.now();
  const outlineProvider = getLLMProvider(outlineProviderName);
  let outlineResponse: Awaited<ReturnType<typeof outlineProvider.call>> | null = null;
  let validation: Rule14ValidationResult | null = null;
  let rule14bValidation: Rule14bValidationResult | null = null;
  let rule15Validation: Rule15ValidationResult | null = null; // v9 C5: LLM judge result
  let attempt = 0;
  let totalJudgeCost = 0;
  let totalRule14bJudgeCost = 0;

  for (attempt = 0; attempt <= MAX_OUTLINE_RETRIES; attempt++) {
    let promptToUse = outlinePrompt;
    const needsRule14Feedback = attempt > 0 && validation && !validation.valid;
    const needsRule14bFeedback = attempt > 0 && rule14bValidation && !rule14bValidation.valid;
    const needsRule15Feedback = attempt > 0 && rule15Validation && !rule15Validation.valid;
    if (needsRule14Feedback || needsRule14bFeedback || needsRule15Feedback) {
      let feedback = `\n\n# RETRY FEEDBACK`;
      if (needsRule14Feedback) {
        const violationList = validation!.illegitimateH2s
          .map((v) => `- "${v.title}" — ${v.reason}`)
          .join("\n");
        feedback += `\nPoprzednia proba naruszyla Rule 14 — te H2 zawieraja primary_entity ale NIE pelnia roli topical anchor (sa keyword stuffing):
${violationList}

Przepisz te H2 anchorujac w realnym sub-discoverable zakresie sekcji (cokolwiek wynika z KG / EAV / scope tematu) BEZ powtarzania primary_entity w naglowku.`;
        logPublic("Outline", `Rule 14 retry ${attempt}/${MAX_OUTLINE_RETRIES} — ${validation!.illegitimateH2s.length} H2 flagged by judge`);
      }
      if (needsRule14bFeedback) {
        const r14bList = rule14bValidation!.flaggedAfterJudge
          .map((f) => {
            const src = f.suggested_anchor_source ? ` [suggested ${f.suggested_anchor_source}` : "";
            const val = f.suggested_anchor_value ? `: "${f.suggested_anchor_value}"]` : src ? "]" : "";
            return `- "${f.title}" — ${f.reason}${src}${val}`;
          })
          .join("\n");
        feedback += `\n\nPoprzednia proba naruszyla Rule 14b — te H2 maja generic structural opener (cross-mapa kopiowalne, brak anchora w konkretnej KG sub-entity / EAV attribute / canonical_question scope):
${r14bList}

Przepisz te H2 wstawiajac KONKRETNE anchors z suggested sources (KG sub-entity name / EAV attribute value / specific canonical_question scope). H2 NIE moze byc 1:1 kopiowalny do outline innego node'a z innym primary_entity.`;
        logPublic("Outline", `Rule 14b retry ${attempt}/${MAX_OUTLINE_RETRIES} — ${rule14bValidation!.flaggedAfterJudge.length} H2 flagged (cross-mapa kopiowalne) verdict=${rule14bValidation!.judgeVerdict}`);
      }
      if (needsRule15Feedback) {
        // Q3 answer: explicit guidance — list full anchor_pool jako available options +
        // judge reason. Sam reason mówi co poszło źle ale nie pokazuje co poprawić.
        const r15List = rule15Validation!.violations
          .map((v) => {
            const anchorsList = v.available_anchors.map((a) => `    - "${a}"`).join("\n");
            return `- Header: "${v.header}"\n  Reason: ${v.reason}\n  Available anchors do zachowania (wybierz min 1):\n${anchorsList}`;
          })
          .join("\n\n");
        feedback += `\n\nRule 7.6 (HEADER ANCHOR PRESERVATION v15) — naruszone:
${r15List}

Przepisz te headers zachowując min 1 anchor (substring verbatim LUB semantic equivalent — np. "5-25 razy" ≡ "5x do 25x", "Customer Retention" ≡ "utrzymanie klienta"). primary_entity NIE jest wymagany — zachowaj INNY konkret z listy ANCHORS.`;
        logPublic("Outline", `Rule 7.6 retry ${attempt}/${MAX_OUTLINE_RETRIES} — ${rule15Validation!.violations.length} H2 flagged by judge`);
      }
      promptToUse += feedback;
    }

    outlineResponse = await outlineProvider.call(promptToUse, {
      model: outlineModel,
      jsonSchema: OUTLINE_SCHEMA,
      temperature: dbPrompt?.temperature ?? 0.4,
      maxTokens: 8000,
      step: "outline_generation",
      mapId,
      nodeId,
    });
    totalCost += outlineResponse.costUsd || 0;

    // Quick parse for judge — full sanitize (G5/G6/G11) below uses same response
    let h2OnlyForJudge: Array<{ header: string; source_area: string | null }> = [];
    try {
      const quickParsed = parseLLMJSON<Record<string, unknown>>(outlineResponse.text);
      const sectionsForJudge = (quickParsed.outline || quickParsed.sections || []) as Array<{ header?: string; type?: string; source_area?: string | null }>;
      // v9 C2: sanitize source_area PRZED walidator (Rule 14/14b/15). Wczesniej (pre-C2)
      // sanitize dzial sie tylko POST-retry → walidatory widzialy pre-normalize source_area,
      // a Rule 15 z empty source_area robil false PASS (linia 605: source_area=="" continue).
      // Teraz walidator widzi finalny dataset; halucynowane source_area zerowane PRZED
      // walidatorem, retry feedback iteruje on truth.
      const sanitizeStats = sanitizeOutlineSourceArea(sectionsForJudge, inputAreaNameByNorm, normalizeAreaName);
      if (sanitizeStats.invalidAreaCount > 0 || sanitizeStats.normalizedCount > 0) {
        logDebug("Outline", `Pre-validator sanitize (attempt ${attempt}): ${sanitizeStats.normalizedCount} normalized, ${sanitizeStats.invalidAreaCount} hallucynowane zerowane`);
      }
      h2OnlyForJudge = sectionsForJudge
        .filter((s) => s.type === "h2" && s.header)
        .map((s) => ({ header: s.header!, source_area: s.source_area ?? null }));
    } catch (err) {
      logPublic("Outline", `Rule 14/14b judge skipped — outline parse failed: ${err instanceof Error ? err.message : String(err)}`);
      validation = { valid: true, illegitimateH2s: [], judgeCostUsd: 0 };
      rule14bValidation = {
        valid: true,
        detectorResult: { triggered: false, generic_opener_count: 0, total_h2_count: 0, flagged_h2s: [] },
        judgeVerdict: "SKIPPED",
        flaggedAfterJudge: [],
        judgeCostUsd: 0,
      };
      rule15Validation = { valid: true, violations: [] };
      break;
    }

    if (h2OnlyForJudge.length === 0) {
      // No H2 to judge — outline structure issue, downstream sanitize handles it
      validation = { valid: true, illegitimateH2s: [], judgeCostUsd: 0 };
      rule14bValidation = {
        valid: true,
        detectorResult: { triggered: false, generic_opener_count: 0, total_h2_count: 0, flagged_h2s: [] },
        judgeVerdict: "SKIPPED",
        flaggedAfterJudge: [],
        judgeCostUsd: 0,
      };
      rule15Validation = { valid: true, violations: [] };
      break;
    }

    validation = await validateOutlineRule14(
      h2OnlyForJudge,
      outlineCtx?.primary_entity || "",
      outlineCtx?.document_role || "",
      outlineCtx?.canonical_question || "",
      subEntities,
      mapId,
      nodeId,
    );
    totalJudgeCost += validation.judgeCostUsd;
    totalCost += validation.judgeCostUsd;

    // Rule 14b — cross-outline structural opener diversity (v13b runtime guard).
    // Detector binary majority gate, judge LLM gpt-4.1-mini z KG+EAV pool.
    const detectorResult = detectGenericOpeners(h2OnlyForJudge, outlineCtx?.document_role || "");
    rule14bValidation = await validateOutlineRule14b(
      h2OnlyForJudge,
      outlineCtx?.primary_entity || "",
      outlineCtx?.document_role || "",
      outlineCtx?.canonical_question || "",
      subEntities,
      ctxEav || [],
      detectorResult,
      mapId,
      nodeId,
    );
    totalRule14bJudgeCost += rule14bValidation.judgeCostUsd;
    totalCost += rule14bValidation.judgeCostUsd;

    // Rule 7.6 — header anchor preservation (P7 v14 regex → v9 C5 LLM judge).
    // gpt-4.1-mini judge ocenia per H2 semantic equivalence z anchor_pool z micro_areas
    // (z C1 commit). PE filter pre-LLM (Q2): code-side wyklucza pool entries których
    // wszystkie tokens to PE tokens. EXCEPTION clause: filtered pool empty → skip judge
    // call (DIRECT_ANSWER definicja entity). Cross-niche cross-language (PL/EN/DE/JP +
    // medical/legal/financial). Soft fallback on parse fail (pattern z Rule 14).
    rule15Validation = await validateOutlineRule15(
      h2OnlyForJudge,
      outlineCtx?.primary_entity || "",
      fanOut.micro_areas,
      mapId,
      nodeId,
    );
    totalCost += rule15Validation.judgeCostUsd;

    if (validation.valid && rule14bValidation.valid && rule15Validation.valid) break;
  }

  if (!outlineResponse) {
    throw new Error("Outline LLM call failed — no response after retry loop");
  }
  const outlineDuration = Date.now() - outlineStart;
  const rule14Debug = {
    attempts: attempt + 1,
    final_valid: validation?.valid ?? false,
    illegitimate_h2s: validation?.illegitimateH2s ?? [],
    judge_cost_usd: totalJudgeCost,
    violation_after_retry: !(validation?.valid ?? true),
  };
  const rule14bDebug = {
    detector_triggered: rule14bValidation?.detectorResult.triggered ?? false,
    generic_opener_count: rule14bValidation?.detectorResult.generic_opener_count ?? 0,
    total_h2_count: rule14bValidation?.detectorResult.total_h2_count ?? 0,
    flagged_before: rule14bValidation?.detectorResult.flagged_h2s ?? [],
    flagged_after: rule14bValidation?.flaggedAfterJudge ?? [],
    judge_verdict: rule14bValidation?.judgeVerdict ?? "SKIPPED",
    judge_cost_usd: totalRule14bJudgeCost,
    attempts: attempt + 1,
    final_valid: rule14bValidation?.valid ?? false,
    violation_after_retry: !(rule14bValidation?.valid ?? true),
  };
  const rule15Debug = {
    attempts: attempt + 1,
    final_valid: rule15Validation?.valid ?? false,
    violations: rule15Validation?.violations ?? [],
    violation_after_retry: !(rule15Validation?.valid ?? true),
    judge_cost_usd: rule15Validation?.judgeCostUsd ?? 0,  // v9 C5: judge cost forensics
    judge_attempts: rule15Validation?.judgeAttempts ?? 0,
  };

  logDebug("Outline", "LLM response stats", { tokens_in: outlineResponse.tokensInput, tokens_out: outlineResponse.tokensOutput, cost: outlineResponse.costUsd?.toFixed(4), duration_ms: outlineDuration, response_length: outlineResponse.text.length, rule_14: rule14Debug, rule_14b: rule14bDebug, rule_15_header_anchor: rule15Debug });
  logVerbose("Outline", `RAW RESPONSE:\n${"─".repeat(60)}\n${outlineResponse.text}\n${"─".repeat(60)}`);
  if (rule14Debug.violation_after_retry) {
    logPublic("Outline", `Rule 14 violation persists after ${MAX_OUTLINE_RETRIES} retries — soft fallback (accept best attempt)`);
  }
  if (rule14bDebug.violation_after_retry) {
    logPublic("Outline", `Rule 14b violation persists after ${MAX_OUTLINE_RETRIES} retries — soft fallback (accept best attempt). Flagged: ${rule14bDebug.flagged_after.length}/${rule14bDebug.total_h2_count}`);
  }
  if (rule15Debug.violation_after_retry) {
    logPublic("Outline", `Rule 7.6 (header anchor) violation persists after ${MAX_OUTLINE_RETRIES} retries — soft fallback. ${rule15Debug.violations.length} H2 with header losing source_area anchors.`);
  }

  const rawOutline = parseLLMJSON<Record<string, unknown>>(outlineResponse.text);
  logDebug("Outline", `Parsed JSON keys: ${Object.keys(rawOutline).join(", ")}`);

  // LLM may return { "sections": [...] } or { "outline": [...] } — handle both
  const outlineData: { sections: OutlineSection[] } = {
    sections: (rawOutline.sections || rawOutline.outline || []) as OutlineSection[],
  };

  if (!Array.isArray(outlineData.sections) || outlineData.sections.length === 0) {
    logError("Outline", `LLM zwrócił outline bez sections! Keys: ${Object.keys(rawOutline).join(", ")}`);
    throw new Error(`Outline LLM nie zwrócił sections. Sprawdź prompt outline_generation.`);
  }

  logDebug("Outline", `Raw sections count: ${outlineData.sections.length}`);

  // Map different field names LLM might use
  for (const section of outlineData.sections) {
    const raw = section as any;
    // LLM may use "heading_level" instead of "type"
    if (!section.type && raw.heading_level) {
      section.type = raw.heading_level === "intro" ? "intro" : "h2";
    }
    if (!section.type) section.type = section.order === 0 ? "intro" : "h2";
    // LLM may use many different keys for the heading text
    if (!section.header) {
      section.header = raw.title || raw.heading || raw.name || raw.label || raw.h2 || raw.text || null;
    }
    // Map h3s that might use different field names
    if (section.h3s && Array.isArray(section.h3s)) {
      section.h3s = section.h3s.map((h3: any) => ({
        header: h3.header || h3.title || h3.heading || h3.name || h3.text || h3.h3 || "",
        format: h3.format || "context",
        source_paa: h3.source_paa,
      }));
    }
    // If h3s came as "subsections" or "children"
    if ((!section.h3s || section.h3s.length === 0) && (raw.subsections || raw.children || raw.sub_sections)) {
      const subs = raw.subsections || raw.children || raw.sub_sections || [];
      if (Array.isArray(subs) && subs.length > 0) {
        section.h3s = subs.map((h3: any) => ({
          header: h3.header || h3.title || h3.heading || h3.name || h3.text || "",
          format: h3.format || "context",
          source_paa: h3.source_paa,
        }));
      }
    }
  }

  // Strip [EAV], [SERP] and similar tags from headers (prompt artifacts, not for output)
  for (const section of outlineData.sections) {
    if (section.header) section.header = section.header.replace(/\s*\[[A-Z]+\]/g, "").trim();
    if (section.h3s) {
      for (const h3 of section.h3s) {
        if (h3.header) h3.header = h3.header.replace(/\s*\[[A-Z]+\]/g, "").trim();
      }
    }
  }

  // v9 C2: Sanitize + validate each section. Walidator przeciw halucynacjom LLM:
  //  - source_intent MUSI byc z zamknietego enum (6 intencji) — inline below
  //  - source_area MUSI byc z input areas — via sanitizeOutlineSourceArea helper
  // VALID_INTENTS + inputAreaNameByNorm + normalizeAreaName built BEFORE retry loop (linia ~990)
  // żeby walidatory Rule 14/14b/15 widziały sanitized dataset. Tu post-retry idempotent
  // safety net dla fresh outlineData.sections (re-parsed osobno od sectionsForJudge).
  let invalidIntentCount = 0;

  // Post-retry source_area sanitize na finalnym dataset (re-parsed osobno od sectionsForJudge).
  const finalSanitize = sanitizeOutlineSourceArea(outlineData.sections, inputAreaNameByNorm, normalizeAreaName);
  const invalidAreaCount = finalSanitize.invalidAreaCount;
  if (finalSanitize.normalizedCount > 0) {
    logDebug("Outline", `Post-retry: normalized ${finalSanitize.normalizedCount} source_area trailing suffixes`);
  }

  for (const section of outlineData.sections) {
    section.h3s = section.h3s || [];
    section.section_type = section.section_type || "full";

    if (section.type === "intro") {
      section.source_intent = "";
      section.source_area = "";
      continue;
    }

    // Validate source_intent ∈ enum
    if (section.source_intent && !VALID_INTENTS.has(section.source_intent)) {
      logDebug("Outline", `Invalid source_intent "${section.source_intent}" → "" (section "${section.header}")`);
      invalidIntentCount++;
      section.source_intent = "";
    } else {
      section.source_intent = section.source_intent || "";
    }
  }

  if (invalidIntentCount + invalidAreaCount > 0) {
    logPublic(
      "Outline",
      `Validation: ${invalidIntentCount} invalid intents, ${invalidAreaCount} invalid areas (halucynacje)`,
    );
  }

  // Log parsed outline structure — full detail
  logPublic("Outline", `Parsed outline: ${outlineData.sections.length} sections`);
  for (const s of outlineData.sections) {
    if (s.type === "intro") {
      logDebug("Outline", `  INTRO (order=${s.order})`);
    } else {
      const sectionType = s.section_type || "full";
      const intent = s.source_intent || "?";
      logDebug("Outline", `  H2: "${s.header}" [${sectionType}, intent=${intent}, order=${s.order}]`);
      if ((s.h3s || []).length > 0) {
        for (const h3 of s.h3s) {
          const paaSource = h3.source_paa ? `source_paa="${h3.source_paa}"` : "NO source_paa";
          const h2Norm = normalizeForDedup(s.header || "");
          const h3Norm = normalizeForDedup(h3.header || "");
          const isDup = h2Norm === h3Norm || (h2Norm.length >= 5 && h3Norm.length >= 5 && (h2Norm.includes(h3Norm) || h3Norm.includes(h2Norm)));
          logDebug("Outline", `    H3: "${h3.header}" [format=${h3.format}, ${paaSource}]${isDup ? " DUPLICATE OF H2" : ""}`);
        }
      } else {
        logDebug("Outline", `    (no H3 — standalone section)`);
      }
    }
  }

  // ---- GUARDRAILS ----

  // G0: Zero context sections jesli input nie ma secondary/definitional intents.
  // LLM czasem wymysla "Diagnostyczna" context section bo widzi je w prompt example —
  // tu je wycinamy gdy pipeline nie dostarczyl danych do kontekstu.
  const hasSecondaryOrDef = secondaryAreas.length + defAreas.length > 0;
  if (!hasSecondaryOrDef) {
    const strayContext = outlineData.sections.filter(
      (s) => s.type === "h2" && s.section_type === "context",
    );
    if (strayContext.length > 0) {
      outlineData.sections = outlineData.sections.filter((s) => !strayContext.includes(s));
      logPublic(
        "Outline",
        `G0: removed ${strayContext.length} stray context sections (zero secondary/definitional intents)`,
      );
      logDebug("Outline", `G0 removed: ${strayContext.map((s) => s.header).join(", ")}`);
    }
  }

  // G1: Max 3 context sections — trim excess
  const contextSections = outlineData.sections.filter((s) => s.type === "h2" && s.section_type === "context");
  if (contextSections.length > 3) {
    const toRemove = contextSections.slice(3);
    outlineData.sections = outlineData.sections.filter((s) => !toRemove.includes(s));
    logPublic("Outline", `G1: trimmed ${toRemove.length} excess context sections (max 3)`);
    logDebug("Outline", `G1 removed: ${toRemove.map((s) => s.header).join(", ")}`);
  }

  // G2: Dedup semantically similar H2 — remove near-duplicate headers
  const h2Sections = outlineData.sections.filter((s) => s.type === "h2" && s.header);
  const h2Norms = h2Sections.map((s) => ({ section: s, norm: normalizeForDedup(s.header || "") }));
  const dupSections = new Set<OutlineSection>();
  for (let i = 0; i < h2Norms.length; i++) {
    if (dupSections.has(h2Norms[i].section)) continue;
    for (let j = i + 1; j < h2Norms.length; j++) {
      if (dupSections.has(h2Norms[j].section)) continue;
      const a = h2Norms[i].norm, b = h2Norms[j].norm;
      if (a === b || (a.length >= 5 && b.length >= 5 && (a.includes(b) || b.includes(a)))) {
        // Keep the one with more H3s, or the first one
        const keepI = (h2Norms[i].section.h3s?.length || 0) >= (h2Norms[j].section.h3s?.length || 0);
        const remove = keepI ? h2Norms[j].section : h2Norms[i].section;
        dupSections.add(remove);
        logDebug("Outline", `G2: removed duplicate H2 "${remove.header}" (similar to "${keepI ? h2Norms[i].section.header : h2Norms[j].section.header}")`);
      }
    }
  }
  // G2b: Detect when LLM split one area into multiple H2s (same source_area)
  const sourceAreaMap = new Map<string, OutlineSection[]>();
  for (const s of outlineData.sections) {
    if (s.type !== "h2" || !s.source_area) continue;
    const norm = normalizeForDedup(s.source_area);
    if (!sourceAreaMap.has(norm)) sourceAreaMap.set(norm, []);
    sourceAreaMap.get(norm)!.push(s);
  }
  for (const [, sections] of sourceAreaMap) {
    if (sections.length <= 1) continue;
    const keeper = sections[0];
    for (let i = 1; i < sections.length; i++) {
      const donor = sections[i];
      keeper.h3s = [...(keeper.h3s || []), ...(donor.h3s || [])];
      dupSections.add(donor);
      logDebug("Outline", `G2b: merged "${donor.header}" into "${keeper.header}" (same source_area: "${donor.source_area}")`);
    }
  }

  if (dupSections.size > 0) {
    outlineData.sections = outlineData.sections.filter((s) => !dupSections.has(s));
  }

  // G3: Re-number orders after removals
  outlineData.sections.forEach((s, i) => { s.order = i; });

  // G4: Cross-section H3 dedup — remove H3 that duplicates another H3 from a different section
  const allH3Norms: Array<{ section: OutlineSection; h3: OutlineH3; norm: string }> = [];
  for (const s of outlineData.sections) {
    for (const h3 of (s.h3s || [])) {
      allH3Norms.push({ section: s, h3, norm: normalizeForDedup(h3.header) });
    }
  }
  const removedH3s: string[] = [];
  for (let i = 0; i < allH3Norms.length; i++) {
    for (let j = i + 1; j < allH3Norms.length; j++) {
      if (allH3Norms[i].section === allH3Norms[j].section) continue;
      const a = allH3Norms[i].norm, b = allH3Norms[j].norm;
      if (a === b || (a.length >= 5 && b.length >= 5 && (a.includes(b) || b.includes(a)))) {
        const secI = allH3Norms[i].section;
        const secJ = allH3Norms[j].section;
        const removeFrom = (secI.h3s?.length || 0) >= (secJ.h3s?.length || 0) ? secJ : secI;
        const removeH3 = removeFrom === secI ? allH3Norms[i].h3 : allH3Norms[j].h3;
        removeFrom.h3s = (removeFrom.h3s || []).filter((h) => h !== removeH3);
        removedH3s.push(`"${removeH3.header}" from H2 "${removeFrom.header}"`);
      }
    }
  }
  if (removedH3s.length > 0) {
    logPublic("Outline", `G4: removed ${removedH3s.length} cross-section duplicate H3s`);
    logDebug("Outline", `G4 details`, { removed: removedH3s });
  }

  // G5: Remove H3 that duplicates parent H2.
  // WZMOCNIONE 2026-04-20: jaccard tokens >= 0.5 (anti-parafraza) + stare exact/substring.
  // User feedback: "Cechy i moduly narzedzi AI" vs "Najwazniejsze cechy i skladniki"
  // mialy wspolne tokeny cechy/skladniki/AI ale zero overlap substringiem 4+ — G5
  // nie lapalo. Teraz jaccard tokens wylapuje semantyczne duplikaty.
  let g5Removed = 0;
  for (const s of outlineData.sections) {
    if (s.type !== "h2" || !s.h3s || s.h3s.length === 0) continue;

    const h2Norm = normalizeForDedup(s.header || "");
    const before = s.h3s.length;
    s.h3s = s.h3s.filter((h3) => {
      const h3Norm = normalizeForDedup(h3.header);
      if (h2Norm.length < 4 || h3Norm.length < 4) return true;

      // Check 1: exact/substring (legacy)
      if (h2Norm === h3Norm || h2Norm.includes(h3Norm) || h3Norm.includes(h2Norm)) {
        logDebug("Outline", `G5: removed H3 "${h3.header}" (substring H2 "${s.header}")`);
        g5Removed++;
        return false;
      }
      // Check 2: jaccard >= 0.5 (tokens overlap). Przyklad: "cechy moduly narzedzi AI"
      // vs "najwazniejsze cechy skladniki" → jaccard ~0.4 — NA GRANICY. Podnioslem
      // do 0.5 zeby zbyt agresywnie nie usuwac. User wskazal 0.5 jako optymalny prog.
      const sim = jaccardSimilarity(h2Norm, h3Norm);
      if (sim >= 0.5) {
        logDebug(
          "Outline",
          `G5: removed H3 "${h3.header}" (jaccard=${sim.toFixed(2)} >= 0.5 — parafraza H2 "${s.header}")`,
        );
        g5Removed++;
        return false;
      }
      return true;
    });

    if (before > 0 && s.h3s.length === 0) {
      logDebug("Outline", `G5: H2 "${s.header}" now standalone`);
    }
  }
  if (g5Removed > 0) {
    logPublic("Outline", `G5: removed ${g5Removed} H3 duplikujace H2 (substring lub jaccard>=0.5)`);
  }

  // G5b: H3-vs-H3 dedup w obrebie tego samego H2 (jaccard >= 0.4).
  // Outline LLM czasem generuje 2 H3 o nakladajacych sie tematach pod jednym H2
  // (kanibalizacja sekcji). G5 sprawdza H3 vs parent H2, ale NIE H3 vs sasiednie H3.
  // Drop tego z slabsza extractable claim (krotszy header = mniej specific).
  let g5bRemoved = 0;
  for (const s of outlineData.sections) {
    if (s.type !== "h2" || !s.h3s || s.h3s.length < 2) continue;
    const dropIdx = new Set<number>();
    for (let i = 0; i < s.h3s.length; i++) {
      if (dropIdx.has(i)) continue;
      const h3iNorm = normalizeForDedup(s.h3s[i].header || "");
      if (h3iNorm.length < 4) continue;
      for (let j = i + 1; j < s.h3s.length; j++) {
        if (dropIdx.has(j)) continue;
        const h3jNorm = normalizeForDedup(s.h3s[j].header || "");
        if (h3jNorm.length < 4) continue;
        const sim = jaccardSimilarity(h3iNorm, h3jNorm);
        if (sim >= 0.4) {
          // Drop weaker claim: shorter header (mniej extractable)
          const lenI = s.h3s[i].header.length;
          const lenJ = s.h3s[j].header.length;
          const dropTarget = lenI <= lenJ ? i : j;
          const keepTarget = dropTarget === i ? j : i;
          dropIdx.add(dropTarget);
          logDebug(
            "Outline",
            `g5_h3_vs_h3_dedup: drop "${s.h3s[dropTarget].header}" (jaccard=${sim.toFixed(2)} >= 0.4 z "${s.h3s[keepTarget].header}", parent H2 "${s.header}")`,
          );
          g5bRemoved++;
        }
      }
    }
    if (dropIdx.size > 0) {
      s.h3s = s.h3s.filter((_, idx) => !dropIdx.has(idx));
      if (s.h3s.length === 0) {
        logDebug("Outline", `G5b: H2 "${s.header}" now standalone after H3-vs-H3 dedup`);
      }
    }
  }
  if (g5bRemoved > 0) {
    logPublic("Outline", `G5b: removed ${g5bRemoved} H3 duplikujace siebie w obrebie tego samego H2 (jaccard >= 0.4)`);
  }

  // G6: Remove H3 ktore duplikuja H1 artykulu (jaccard > 0.6).
  // Przypadek: H1="Co to jest growth hacking i czym rozni sie od marketingu?",
  // LLM wygenerowal H3="Czym jest growth hacking?" — 90% ta sama tresc, user
  // widzi pytanie 2 razy (H1 + H3). PAA dedup sam w sobie nie lapie bo PAA
  // moze brzmiec inaczej niz H1 mimo tej samej intencji.
  {
    const h1Norm = normalizeForDedup(seoTitle);
    const canonicalNorm = normalizeForDedup(outlineCtx?.canonical_question || seoTitle);
    let h1DupCount = 0;
    for (const s of outlineData.sections) {
      if (s.type !== "h2" || !s.h3s) continue;
      s.h3s = s.h3s.filter((h3) => {
        const h3Norm = normalizeForDedup(h3.header || "");
        if (h3Norm.length < 4) return true;
        const simH1 = jaccardSimilarity(h3Norm, h1Norm);
        const simCanonical = jaccardSimilarity(h3Norm, canonicalNorm);
        const sim = Math.max(simH1, simCanonical);
        if (sim > 0.6) {
          logDebug(
            "Outline",
            `G6: removed H3 "${h3.header}" (dup H1 jaccard=${sim.toFixed(2)}, parent H2 "${s.header}")`,
          );
          h1DupCount++;
          return false;
        }
        return true;
      });
    }
    if (h1DupCount > 0) {
      logPublic("Outline", `G6: removed ${h1DupCount} H3 duplicating H1 (redundancja tytul↔H3)`);
    }
  }

  // ---- SRO GUARDRAILS (W4, SRO-compliance checks) ----
  // Helper: tokens z primary_entity (do strip przed jaccard comparison).
  const primaryEntityTokens = (outlineCtx?.primary_entity || "")
    .toLowerCase()
    .split(/\s+/)
    .filter((t) => t.length > 2);
  const stripEntityTokens = (s: string): string => {
    let result = s;
    for (const t of primaryEntityTokens) {
      result = result.replace(new RegExp(`\\b${t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "gi"), "");
    }
    return result.replace(/\s+/g, " ").trim();
  };

  // G7: CANONICAL_ANSWER_COVERAGE (SRO 4.1) — role=definition only.
  // Pierwszy non-intro H2 powinien matchowac canonical_question (after strip primary_entity).
  // Soft warn — nie reject, bo LLM moze legit miec pierwszy H2 [PROVENANCE] (np. role=history).
  if (role === "definition") {
    const firstH2 = outlineData.sections.find((s) => s.type === "h2");
    if (firstH2?.header && outlineCtx?.canonical_question) {
      const canonNorm = stripEntityTokens(normalizeForDedup(outlineCtx.canonical_question));
      const h2Norm = stripEntityTokens(normalizeForDedup(firstH2.header));
      const sim = canonNorm && h2Norm ? jaccardSimilarity(canonNorm, h2Norm) : 0;
      if (sim < 0.2 && canonNorm.length > 3) {
        logPublic(
          "Outline",
          `G7 WARN: pierwszy H2 off-canonical (role=definition). H2="${firstH2.header}" vs canonical="${outlineCtx.canonical_question}" (jaccard=${sim.toFixed(2)})`,
        );
      }
    }
  }

  // G8: NUMBER_IN_H2 ↔ H3_COUNT (auto-fix: strip liczbe gdy nie matchuje).
  // H2 "5 rzeczy..." deklaruje 5, ma 2 H3 → user widzi fake liczba.
  // Auto-strip liczby z H2 jesli actual_h3_count < declared_count.
  //
  // P6 fix (v14c): G8 byl false-positive na MEASURABLE patterns gdzie liczba
  // jest czescia measurable, NIE template count. Empirycznie ICE H2.2:
  // header "Ocena Impact, Confidence i Ease w skali 1-5 points" → G8 zmatchowal "5"
  // w "1-5" → strip "5 " → "1-points" → render "1-punktów" (gramatyka rozsypana).
  //
  // Universal cross-language (regex strukturalny: numbers + dashes + slashes + units).
  // Skip patterns:
  // - "X na Y" / "X do Y" / "X z Y" (proporcje)
  // - "X-Y" / "X–Y" (ranges, ASCII hyphen + en-dash)
  // - "X/Y" (fractions)
  // - "X%" / "X‰" (percentages)
  // - "X.Y" / "X,Y" (decimals)
  // - 4-digit years (1900-2099)
  // - "X procent" / "X punkt" / "X raz" / "Xx" (numerical units)
  const MEASURABLE_PATTERNS: RegExp[] = [
    /\b\d+\s+(?:na|do|z)\s+\d+\b/,                  // "1 na 8", "5 do 10"
    /\b\d+\s*[-–]\s*\d+\b/,                          // "1-5", "5–25"
    /\b\d+\s*\/\s*\d+\b/,                            // "1/8", "5/10"
    /\b\d+[%‰]/,                                     // "50%", "12.5%"
    /\b\d+[.,]\d+\b/,                                // "12.5", "2,5"
    /\b(?:19|20)\d{2}\b/,                            // years 1900-2099
    /\b\d+\s*(?:proc(?:ent)?|punkt|pkt|raz|x)\b/i,   // "50 procent", "5 punktów", "5 razy", "5x"
  ];

  let g8Stripped = 0;
  let g8SkippedMeasurable = 0;
  for (const s of outlineData.sections) {
    if (s.type !== "h2" || s.section_type === "context") continue;
    if (!s.header) continue;

    // P6: skip jesli header zawiera MEASURABLE pattern — number jest czescia
    // measurable (range/proportion/scale/date), NIE template count.
    const hasMeasurable = MEASURABLE_PATTERNS.some((p) => p.test(s.header!));
    if (hasMeasurable) {
      g8SkippedMeasurable++;
      logDebug(
        "Outline",
        `G8: H2 "${s.header}" zawiera measurable pattern → skip strip (P6 v14c)`,
      );
      continue;
    }

    const numMatch = s.header.match(/\b([3-9]|10)\b/);
    if (!numMatch) continue;
    const declared = parseInt(numMatch[1]);
    const actual = (s.h3s || []).length;
    if (actual < declared) {
      const stripped = s.header.replace(/\b([3-9]|10)\s+/, "").trim();
      logDebug(
        "Outline",
        `G8: H2 "${s.header}" deklaruje ${declared}, ma ${actual} H3 → strip liczby → "${stripped}"`,
      );
      s.header = stripped;
      g8Stripped++;
    }
  }
  if (g8Stripped > 0 || g8SkippedMeasurable > 0) {
    logPublic(
      "Outline",
      `G8: stripped ${g8Stripped} fake numbers z H2 headers (skipped ${g8SkippedMeasurable} measurable patterns)`,
    );
  }

  // G9: H2 ↔ source_area OVERLAP (soft warn, SRO topic alignment).
  // Jesli LLM przykleil source_area ale header o czym innym → warn.
  //
  // Soft PL stem matching: jaccardSimilarity ze strict equality nie lapie
  // odmian polskich ("kanoniczny"/"kanoniczne" jaccard=0). Lokalny softJaccard
  // uznaje 2 tokeny za match jesli dziela 5+ pierwszych znakow (5-char stem).
  // Dla G9 soft-warn to wystarczy. G7 (canonical) i G10 (entity anchor) zostaja
  // przy strict jaccard — tam dokladnosc pozadana.
  const softTokenMatch = (a: string, b: string): boolean => {
    if (a === b) return true;
    if (a.length < 5 || b.length < 5) return false;
    return a.slice(0, 5) === b.slice(0, 5);
  };
  const softJaccard = (a: string, b: string): number => {
    const ta = a.split(/\s+/).filter(Boolean);
    const tb = b.split(/\s+/).filter(Boolean);
    if (ta.length === 0 || tb.length === 0) return 0;
    const matched = ta.filter((x) => tb.some((y) => softTokenMatch(x, y))).length;
    const union = new Set([...ta, ...tb]).size;
    return union > 0 ? matched / union : 0;
  };

  let g9Offtopic = 0;
  for (const s of outlineData.sections) {
    if (s.type !== "h2" || !s.source_area || !s.header) continue;
    // Strip tag kategorii [XYZ] z source_area przed jaccard (tag to tylko metadana)
    const areaClean = s.source_area.replace(/^\s*\[[A-Z_]+\]\s*/, "").replace(/\s*\[eav\]\s*$/i, "");
    const areaNorm = stripEntityTokens(normalizeForDedup(areaClean));
    const h2Norm = stripEntityTokens(normalizeForDedup(s.header));
    if (!areaNorm || !h2Norm || areaNorm.length < 4) continue;
    const sim = softJaccard(areaNorm, h2Norm);
    if (sim < 0.15) {
      logDebug(
        "Outline",
        `G9: H2 off-topic vs source_area. H2="${s.header}" source="${s.source_area}" (softJaccard=${sim.toFixed(2)})`,
      );
      g9Offtopic++;
    }
  }
  if (g9Offtopic > 0) {
    logPublic("Outline", `G9: ${g9Offtopic} H2 off-topic wobec source_area (SRO topic alignment, soft PL stem)`);
  }

  // G11: H3 source_paa alignment z parent H2 SRO tag.
  // Outline LLM czasem przypisuje H3 z PAA pochodzacym z innej area niz parent H2.
  // Np. H2 [DIRECT_ANSWER] (definicja) ma H3 z source_paa pochodzacym z area [SYMPTOMS]
  // (objawy) → misalignment, kanibalizacja sekcji + utrata SRO topic clarity.
  // Smoke test 14:38 node 1: H2-1 [DIRECT_ANSWER] mial H3 "Lek przed..." z source_paa
  // pochodzacym z area [SYMPTOMS] — drop poprzez G11.
  let g11Misaligned = 0;
  for (const s of outlineData.sections) {
    if (s.type !== "h2" || !s.h3s || !s.source_area) continue;
    const parentTag = s.source_area.match(/\[([A-Z_]+)\]/)?.[1];
    if (!parentTag) continue;
    const beforeCount = s.h3s.length;
    s.h3s = s.h3s.filter((h3) => {
      if (!h3.source_paa) return true; // H3 bez PAA (context-generated) → keep
      const paaNorm = normalizeForDedup(h3.source_paa);
      const originTag = paaToOriginTag.get(paaNorm);
      if (!originTag) return true; // PAA nie w mapie → keep (no signal)
      if (originTag === parentTag) return true; // OK alignment
      logDebug(
        "Outline",
        `g11_h3_source_paa_misalignment: drop "${h3.header}" — parent_tag=[${parentTag}] origin_tag=[${originTag}], source_paa="${h3.source_paa}"`,
      );
      g11Misaligned++;
      return false;
    });
    if (beforeCount > 0 && s.h3s.length === 0) {
      logDebug("Outline", `G11: H2 "${s.header}" now standalone after misalignment drop`);
    }
  }
  if (g11Misaligned > 0) {
    logPublic("Outline", `G11: dropped ${g11Misaligned} H3 z source_paa pochodzacym z innego SRO tag niz parent H2 (kanibalizacja)`);
  }

  // ---- PART B: KG Distribution ----

  // F1a: Distribution LLM widzi source_area (z tag kategorii [BOUNDARIES]/[EVIDENCE]/...)
  // zeby lepiej przypisywac encje tematycznie. Bez tego LLM widzial tylko header
  // + section_type i nie rozrozniał sekcji semantycznie.
  const sectionsJson = outlineData.sections
    .map((s) => {
      const area = s.source_area ? ` [source_area: "${s.source_area}"]` : "";
      return `Section ${s.order}: "${s.header || "intro"}" (${s.section_type})${area}`;
    })
    .join("\n");

  const entitiesJson = (kg.entities || [])
    .map((e) => `${e.entity_id}: ${e.entity_name} (${e.type}${e.strength ? `, strength:${e.strength}` : ""})`)
    .join("\n");

  // Sort facts: high priority first (v2.1), then by confidence
  const sortedFacts = [...(kg.facts || [])].sort((a, b) => {
    const prioOrder: Record<string, number> = { high: 0, medium: 1, low: 2 };
    const prioA = prioOrder[a.priority || "medium"] ?? 1;
    const prioB = prioOrder[b.priority || "medium"] ?? 1;
    if (prioA !== prioB) return prioA - prioB;
    return b.confidence - a.confidence;
  });

  const factsJson = sortedFacts
    .map((f) => `${f.fact_id}: ${f.fact} [${f.category || "general"}, ${f.priority || "medium"}, conf:${f.confidence}]`)
    .join("\n");

  const ideationsJson = (kg.ideations || [])
    .map((i) => `${i.ideation_id}: ${i.description || i.ideation}${i.priority ? ` [${i.priority}]` : ""}`)
    .join("\n");

  const relsJson = (kg.entities_relationships || [])
    .filter((r) => r && r.entity_id2text && Array.isArray(r.entity_id2text))
    .map((r) => `${r.relationship_id}: ${r.entity_id2text[0] || "?"} → ${r.predicate || "?"} → ${r.entity_id2text[1] || "?"}`)
    .join("\n");

  const distributionPrompt = `Przypisz elementy Knowledge Graph do sekcji outline. Zwróć TYLKO JSON, bez komentarzy, bez markdown.

## Sekcje
${sectionsJson}

## Entities
${entitiesJson || "(none)"}

## Facts
${factsJson || "(none)"}

## Ideations
${ideationsJson || "(none)"}

## Relationships
${relsJson || "(none)"}

## Zasady
1. Każdy element do NAJBARDZIEJ RELEWANTNEJ sekcji
2. NIE wszystko musi być użyte (60-80% coverage OK)
3. INTRO: Tylko 2-3 kluczowe encje, 1-2 główne fakty
4. Full sections → więcej danych, Context sections → mniej

## FORMAT — zwróć DOKŁADNIE taki JSON:
{
  "assignments": [
    { "section_order": 0, "entity_ids": ["ent_001"], "fact_ids": ["fact_001"], "ideation_ids": [], "relationship_ids": [] },
    { "section_order": 1, "entity_ids": [...], "fact_ids": [...], "ideation_ids": [...], "relationship_ids": [...] }
  ]
}

Zwróć TYLKO JSON. Bez markdown, bez komentarzy, bez tekstu przed ani po JSON.`;

  const distProvider = getLLMProvider(dbPrompt?.provider || stageConfig.provider);
  const distResponse = await distProvider.call(distributionPrompt, {
    model: dbPrompt?.modelId || stageConfig.model,
    jsonSchema: DISTRIBUTION_SCHEMA,
    temperature: dbPrompt?.temperature ?? 0.3,
    maxTokens: 1500, // Distribution JSON max ~500 tokens, cap at 1500
    step: "outline_generation", mapId, nodeId,
  });
  totalCost += distResponse.costUsd || 0;

  let distData: { assignments: Array<SectionDistribution & { section_order: number }> };
  try {
    distData = parseLLMJSON<{ assignments: Array<SectionDistribution & { section_order: number }> }>(
      distResponse.text,
    );
    if (!distData.assignments || !Array.isArray(distData.assignments)) {
      throw new Error("No assignments array");
    }
  } catch (parseErr) {
    // Fallback: distribute KG items evenly across sections
    logError("Outline", "Distribution parse failed, using fallback", parseErr);
    const h2Sections = outlineData.sections.filter((s) => s.type === "h2");
    const allEntityIds = (kg.entities || []).map((e) => e.entity_id);
    const allFactIds = (kg.facts || []).map((f) => f.fact_id);
    const allIdeationIds = (kg.ideations || []).map((i) => i.ideation_id);

    distData = {
      assignments: outlineData.sections.map((s, i) => ({
        section_order: s.order ?? i,
        entity_ids: s.type === "intro" ? allEntityIds.slice(0, 3) : allEntityIds.slice(i * 3, i * 3 + 5),
        fact_ids: s.type === "intro" ? allFactIds.slice(0, 2) : allFactIds.slice(i * 2, i * 2 + 3),
        ideation_ids: s.type === "intro" ? [] : allIdeationIds.slice(i, i + 1),
        relationship_ids: [],
      })),
    };
  }

  // Enrichment: dla kazdego KG placeholder ID (ent_001, ent_map_X) probuj zmapowac
  // na thematic_entities.id (UUID). NER entities nigdy nie zapisane w DB → skip.
  // Dodajemy entity_db_ids JAKO OSOBNE POLE — entity_ids zostaje bez zmian,
  // draftGenerator dalej dziala (matchuje po in-memory kg.entities.entity_id).
  const enrichToDbIds = (kgIds: string[]): string[] => {
    if (!entityNameToDbId || Object.keys(entityNameToDbId).length === 0) return [];
    const dbIds: string[] = [];
    for (const kgId of kgIds) {
      const kgEntity = kg.entities.find((e) => e.entity_id === kgId);
      if (!kgEntity) continue;
      const dbId = entityNameToDbId[kgEntity.entity_name.toLowerCase()];
      if (dbId) dbIds.push(dbId);
    }
    return dbIds;
  };

  // Build distribution map
  // UWAGA: entity_ids TRZYMA KG placeholders (ent_001, ent_map_X) — to TAK MA BYC.
  // draftGenerator.ts:152 matchuje po kg.entities.entity_id w pamieci, a kg.entities
  // tez ma entity_id jako string (ent_001/ent_map_X). Zmiana na UUID zlamalaby draft.
  // entity_db_ids = SEPARATE pole dla downstream potrzebujacego UUID (analytics,
  // per-section EAV fetch). Moze byc krotsze — NER entities nie sa w DB.
  const distribution: Record<number, SectionDistribution> = {};
  let totalKgIds = 0;
  let totalDbIds = 0;
  for (const assignment of distData.assignments) {
    const entityIds = assignment.entity_ids || [];
    const dbIds = enrichToDbIds(entityIds);
    totalKgIds += entityIds.length;
    totalDbIds += dbIds.length;
    distribution[assignment.section_order] = {
      entity_ids: entityIds,
      entity_db_ids: dbIds,
      fact_ids: assignment.fact_ids || [],
      ideation_ids: assignment.ideation_ids || [],
      relationship_ids: assignment.relationship_ids || [],
    };
  }

  if (totalKgIds > 0) {
    logPublic(
      "Outline",
      `Distribution: ${totalKgIds} KG entity_ids → ${totalDbIds} UUID resolved (${Math.round((totalDbIds / totalKgIds) * 100)}%). ` +
      `Placeholders "ent_XXX" dla draft (in-memory match). UUID w entity_db_ids dla downstream.`,
    );
  }

  // G10: H3 ENTITY ANCHORING (SRO 4.3, soft-warn, nie reject).
  // H3 powinno zawierac primary_entity lub sub-entity z CALEGO kg.entities
  // (nie tylko distribution[section.order]) albo z ctxEav entity_names.
  //
  // FIX F2 (2026-04-20): Wczesniej match tylko po distribution[s.order].entity_ids —
  // Distribution LLM moze przypisac encje (np. "Hotmail") do intro zamiast sekcji 4,
  // wtedy G10 nie widzi jej. Fix: iteracja po calym kg.entities, token-level.
  // FIX F3: dodatkowo uwzgledniamy entity_names z ctxEav (dla encji EAV-level).
  //
  // Token-level match zamiast substring: "hotmail" pasuje do "hotmail inc." w KG
  // (substring fail, token includes success). Stopwords + primary_entity tokens
  // odfiltrowane zeby nie matchowac na "growth"/"hacking" (w kazdym H3).
  // STOPWORDS_PL — rozszerzona lista 3+ char po F3 extension (length > 2)
  // zeby zmiescic akronimy CAC/SEO/MVP/AI bez przepuszczania krotkich PL slowek.
  const STOPWORDS_PL = new Set([
    "jest", "dla", "albo", "lub", "oraz", "jako", "przy", "pod", "nad", "przed",
    "przez", "bez", "miedzy", "mimo", "zamiast", "tylko", "takze", "rowniez",
    "ktore", "ktora", "ktory", "jaki", "jaka", "jakie", "jakiego", "tego",
    "tej", "tym", "twoj", "twoja", "moj", "moja", "swoj", "swoja",
    "moze", "musi", "moge", "trzeba", "warto", "bardzo", "wiecej", "wiele",
    "marketing", "marketingu", "firma", "firmy", "biznes", "biznesu",
    // Dodane dla length>2 (zapobiega przeciekom PL stop-words po rozluznieniu filtra):
    "nie", "tak", "ten", "ta", "to", "nim", "ich", "was", "cos", "ale", "czy",
    "gdy", "kto", "oto", "juz", "tez", "jeszcze", "jak", "ani", "tez",
  ]);
  const primaryEntityTokensLc = new Set(primaryEntityTokens.map((t) => t.toLowerCase()));
  const kgEntityTokens = new Set<string>();
  for (const e of kg.entities || []) {
    const norm = normalizeForDedup(e.entity_name || "");
    for (const t of norm.split(/\s+/)) {
      if (t.length > 2 && !STOPWORDS_PL.has(t) && !primaryEntityTokensLc.has(t)) {
        kgEntityTokens.add(t);
      }
    }
  }
  // F3: ctxEav entity_names (zwykle tylko primary_entity, ale zostawione na przyszlosc
  // gdy bedzie sub-entity EAV typu "Hotmail.podpis_emailowy")
  for (const a of ctxEav || []) {
    const norm = normalizeForDedup(a.entity_name || "");
    for (const t of norm.split(/\s+/)) {
      if (t.length > 2 && !STOPWORDS_PL.has(t) && !primaryEntityTokensLc.has(t)) {
        kgEntityTokens.add(t);
      }
    }
  }
  // F3 extension (KROK 1 / 2026-04-22): tokeny z EAV VALUES (proper nouns Hotmail/
  // Dropbox/Airbnb są w EAV "kanoniczne_przyklady" value, nie w entity_name — bez
  // tego G10 false-positive na H3 z tymi nazwami). Split po `/[\s,+\/()]+/` lapie
  // "X, Y, Z" i "A/B". length > 2 lapie 3-literowe akronimy CAC/SEO/MVP/AI.
  for (const a of ctxEav || []) {
    const norm = normalizeForDedup(a.value || "");
    for (const t of norm.split(/[\s,+\/()]+/)) {
      if (t.length > 2 && !STOPWORDS_PL.has(t) && !primaryEntityTokensLc.has(t)) {
        kgEntityTokens.add(t);
      }
    }
  }

  let g10NoEntity = 0;
  let g10Total = 0;
  for (const s of outlineData.sections) {
    if (s.type !== "h2" || !s.h3s) continue;
    for (const h3 of s.h3s) {
      const h3NormFull = normalizeForDedup(h3.header || "");
      if (h3NormFull.length < 4) continue;
      g10Total++;
      const hasPrimaryEntity = primaryEntityTokens.some((t) => h3NormFull.includes(t));
      // Token-level match po kg.entities globalnie (F2) + ctxEav (F3)
      const h3Tokens = h3NormFull.split(/\s+/);
      const hasAnyKgEntity = h3Tokens.some((ht) => ht.length > 2 && kgEntityTokens.has(ht));
      if (!hasPrimaryEntity && !hasAnyKgEntity) {
        logDebug(
          "Outline",
          `G10 soft-warn: H3 bez entity anchor. H3="${h3.header}" (pod H2 "${s.header}")`,
        );
        g10NoEntity++;
      }
    }
  }
  if (g10Total > 0) {
    const pct = Math.round(((g10Total - g10NoEntity) / g10Total) * 100);
    logPublic(
      "Outline",
      `G10: H3 entity anchoring ${g10Total - g10NoEntity}/${g10Total} (${pct}% OK). ${g10NoEntity} bez entity (SRO 4.3 soft-warn). KG tokens pool: ${kgEntityTokens.size}.`,
    );
  }

  const outline: Outline = {
    sections: outlineData.sections,
    distribution,
  };

  // Save to DB
  await (supabase as any)
    .from("thematic_node_content")
    .update({
      outline: outline.sections,
      distribution: outline.distribution,
    })
    .eq("node_id", nodeId);

  const debug = {
    h2_sections: outline.sections.filter(s => s.type === "h2").length,
    h3_total: outline.sections.reduce((sum, s) => sum + (s.h3s?.length || 0), 0),
    kg_items_distributed: Object.values(outline.distribution).reduce((sum, d) =>
      sum + d.entity_ids.length + d.fact_ids.length + d.ideation_ids.length + d.relationship_ids.length, 0),
    cost_usd: totalCost,
    prompt: outlinePrompt, // forensics: full prompt sent to LLM (verify active prompt version, debug future patches via SQL grep on engine_debug->outline->prompt)
    rule_14: rule14Debug, // v11 LLM-judge guard forensics
    rule_14b: rule14bDebug, // v13b cross-outline structural opener diversity guard forensics
    rule_15_header_anchor: rule15Debug, // v14 P7 header anchor preservation forensics
  };

  logStage("OUTLINE", "END", { h2_sections: debug.h2_sections, h3_total: debug.h3_total, kg_distributed: debug.kg_items_distributed, cost: totalCost });
  return { outline, costUsd: totalCost, debug };
}
